import { supabase } from "./client";
import { getRuleField, matchesLinkedRule, resolveLinkedRuleProducts } from "../menu/linkedRules";
import { getAssistantMenuCatalog, isAssistantStandaloneProduct, restrictAssistantDecisionToMenu } from "./edge-functions/_shared/assistantMenuVisibility";

const PUBLIC_MENU_AI_FUNCTION_NAME = "public-menu-ai-assistant";

const currencyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 2,
});

const toSafeText = (value, maxLength = 240) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const normalizeSearchText = (value) =>
  toSafeText(value, 1600)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const tokenize = (value) =>
  normalizeSearchText(value)
    .split(/[^a-z0-9]+/g)
    .filter((token) => token.length >= 3);

const QUERY_STOP_WORDS = new Set([
  "que",
  "tenes",
  "tiene",
  "tienen",
  "hay",
  "para",
  "con",
  "sin",
  "una",
  "uno",
  "unos",
  "unas",
  "del",
  "las",
  "los",
  "por",
  "me",
  "recomendas",
  "recomendar",
  "recomendacion",
]);

const singularizeToken = (token) => {
  if (token.length > 5 && token.endsWith("es")) {
    return token.slice(0, -2);
  }

  if (token.length > 4 && token.endsWith("s")) {
    return token.slice(0, -1);
  }

  return token;
};

const GASTRONOMY_ONTOLOGY_FAMILIES = [
  {
    key: "apanados",
    aliases: [
      "apanado",
      "apanados",
      "milanesa",
      "milanesas",
      "mila",
      "milas",
      "suprema",
      "supremas",
      "napolitana",
      "napolitanas",
    ],
  },
  {
    key: "pastas",
    aliases: [
      "pasta",
      "pastas",
      "fideo",
      "fideos",
      "tallarin",
      "tallarines",
      "raviol",
      "ravioles",
      "sorrentino",
      "sorrentinos",
      "noqui",
      "noquis",
      "nhoqui",
      "nhoquis",
      "espagueti",
      "spaghetti",
      "agnolotti",
      "canelon",
      "canelones",
      "lasana",
      "lasagna",
    ],
  },
  {
    key: "salsas",
    aliases: [
      "salsa",
      "salsas",
      "bolognesa",
      "blanca",
      "mixta",
      "pesto",
      "fileto",
      "rosa",
    ],
  },
  {
    key: "papas_guarniciones",
    aliases: [
      "papa",
      "papas",
      "frita",
      "fritas",
      "pure",
      "pures",
      "guarnicion",
      "guarniciones",
      "acompanamiento",
      "acompanamientos",
    ],
  },
  {
    key: "ensaladas",
    aliases: [
      "ensalada",
      "ensaladas",
      "verde",
      "verdes",
      "cesar",
      "caprese",
      "mixta",
    ],
  },
  {
    key: "bebidas",
    aliases: [
      "bebida",
      "bebidas",
      "tomar",
      "beber",
      "gaseosa",
      "gaseosas",
      "agua",
      "aguas",
      "jugo",
      "jugos",
      "cerveza",
      "cervezas",
      "vino",
      "vinos",
      "trago",
      "tragos",
      "coctel",
      "cocteles",
      "licuado",
      "licuados",
      "smoothie",
      "smoothies",
      "cafe",
      "cafes",
      "te",
      "tes",
      "infusion",
      "infusiones",
    ],
  },
  {
    key: "bebidas_sin_alcohol",
    aliases: [
      "sin alcohol",
      "sinalcohol",
      "gaseosa",
      "gaseosas",
      "agua",
      "aguas",
      "jugo",
      "jugos",
      "licuado",
      "licuados",
      "smoothie",
      "smoothies",
      "cafe",
      "cafes",
      "te",
      "tes",
      "limonada",
      "limonadas",
      "soda",
      "sodas",
    ],
  },
  {
    key: "bebidas_con_alcohol",
    aliases: [
      "con alcohol",
      "alcohol",
      "cerveza",
      "cervezas",
      "vino",
      "vinos",
      "trago",
      "tragos",
      "aperitivo",
      "aperitivos",
      "coctel",
      "cocteles",
      "fernet",
      "gin",
      "spritz",
    ],
  },
  {
    key: "desayuno_merienda",
    aliases: [
      "desayuno",
      "merienda",
      "cafeteria",
      "cafe",
      "cafes",
      "te",
      "tes",
      "medialuna",
      "medialunas",
      "tostada",
      "tostadas",
      "tostado",
      "tostados",
    ],
  },
  {
    key: "postres",
    aliases: [
      "postre",
      "postres",
      "dulce",
      "dulces",
      "helado",
      "helados",
      "torta",
      "tortas",
      "flan",
      "budin",
      "budines",
      "brownie",
      "brownies",
    ],
  },
  {
    key: "hamburguesas",
    aliases: [
      "hamburguesa",
      "hamburguesas",
      "burger",
      "burgers",
      "medallon",
      "medallones",
    ],
  },
  {
    key: "sandwiches",
    aliases: [
      "sandwich",
      "sandwiches",
      "sanguche",
      "sanguches",
      "lomito",
      "lomitos",
      "lomo",
      "lomos",
      "tostado",
      "tostados",
      "pancho",
      "panchos",
    ],
  },
  {
    key: "pizzas",
    aliases: [
      "pizza",
      "pizzas",
      "muzza",
      "muzzarella",
      "fugazza",
      "fugazzeta",
      "faina",
      "napolitana",
    ],
  },
  {
    key: "carnes_parrilla",
    aliases: [
      "carne",
      "carnes",
      "parrilla",
      "asado",
      "bife",
      "pollo",
      "cerdo",
      "vacuno",
      "bondiola",
      "churrasco",
    ],
  },
  {
    key: "entradas",
    aliases: [
      "entrada",
      "entradas",
      "picada",
      "picadas",
      "empanada",
      "empanadas",
      "tabla",
      "tablas",
      "tapa",
      "tapas",
    ],
  },
];

const buildGastronomyOntologyMaps = () => {
  const aliasMap = new Map();
  const familyMap = new Map();

  GASTRONOMY_ONTOLOGY_FAMILIES.forEach((family) => {
    const lexicon = Array.from(
      new Set(
        [family.key, ...(Array.isArray(family.aliases) ? family.aliases : [])]
          .flatMap((entry) =>
            tokenize(entry).flatMap((token) => [token, singularizeToken(token)])
          )
          .filter((token) => token.length >= 3)
      )
    );

    lexicon.forEach((token) => {
      if (!aliasMap.has(token)) {
        aliasMap.set(token, new Set());
      }

      if (!familyMap.has(token)) {
        familyMap.set(token, new Set());
      }

      lexicon.forEach((relatedToken) => aliasMap.get(token).add(relatedToken));
      familyMap.get(token).add(family.key);
    });
  });

  return {
    aliasMap,
    familyMap,
  };
};

const GASTRONOMY_ONTOLOGY = buildGastronomyOntologyMaps();

const inferGastronomyFamilies = (value) => {
  const families = new Set();

  tokenize(value).forEach((token) => {
    [token, singularizeToken(token)].forEach((normalizedToken) => {
      (GASTRONOMY_ONTOLOGY.familyMap.get(normalizedToken) || []).forEach(
        (familyKey) => families.add(familyKey)
      );
    });
  });

  return Array.from(families);
};

const scoreOntologyFamilyOverlap = (question, values = []) => {
  const questionFamilies = inferGastronomyFamilies(question);

  if (!questionFamilies.length) {
    return 0;
  }

  const targetFamilies = new Set(
    (Array.isArray(values) ? values : [values]).flatMap((value) =>
      inferGastronomyFamilies(value)
    )
  );

  return questionFamilies.reduce(
    (total, familyKey) => total + (targetFamilies.has(familyKey) ? 1 : 0),
    0
  );
};

const expandQueryTokens = (value) => {
  const expanded = new Set();

  tokenize(value).forEach((token) => {
    if (QUERY_STOP_WORDS.has(token)) {
      return;
    }

    const singular = singularizeToken(token);
    expanded.add(token);
    expanded.add(singular);
    (GASTRONOMY_ONTOLOGY.aliasMap.get(token) || []).forEach((synonym) =>
      expanded.add(synonym)
    );
    (GASTRONOMY_ONTOLOGY.aliasMap.get(singular) || []).forEach((synonym) =>
      expanded.add(synonym)
    );
  });

  return Array.from(expanded).filter((token) => token.length >= 3);
};

const normalizeAssistantSuggestions = (value) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => toSafeText(entry, 80))
    .filter(Boolean)
    .filter((entry, index, collection) => collection.indexOf(entry) === index)
    .slice(0, 4);

const normalizeAssistantStringList = (value, maxItems = 16, maxLength = 160) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => toSafeText(entry, maxLength))
    .filter(Boolean)
    .filter((entry, index, collection) => collection.indexOf(entry) === index)
    .slice(0, maxItems);

const isLikelyMeasurementToken = (value = "") =>
  /^\d+(?:ml|cc|g|gr|kg|l|lt|lts)$/i.test(String(value || "").trim());

const looksLikeAssistantCodeToken = (token = "") => {
  const safeToken = toSafeText(token, 32);

  if (!safeToken || safeToken.length < 4 || safeToken.length > 12) {
    return false;
  }

  return (
    /[a-z]/i.test(safeToken) &&
    /\d/.test(safeToken) &&
    !isLikelyMeasurementToken(safeToken) &&
    /^[a-z0-9-]+$/i.test(safeToken)
  );
};

const stripLeadingAssistantCodeFragments = (value = "") => {
  const safeValue = String(value || "")
    .replace(/\r/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!safeValue) {
    return "";
  }

  const tokens = safeValue.split(" ");
  let currentIndex = 0;

  while (currentIndex < tokens.length && currentIndex < 3) {
    const token = tokens[currentIndex].replace(/^[-:|,.;]+|[-:|,.;]+$/g, "");

    if (!looksLikeAssistantCodeToken(token)) {
      break;
    }

    currentIndex += 1;
  }

  return tokens.slice(currentIndex).join(" ").trim() || safeValue;
};

const sanitizeAssistantDisplayName = (value = "", fallback = "Producto") => {
  const normalized = stripLeadingAssistantCodeFragments(toSafeText(value, 160));
  return normalized || fallback;
};

const extractAssistantJsonObjectText = (value = "") => {
  const trimmed = String(value || "").trim();

  if (!trimmed) {
    return "";
  }

  const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  const candidate = fencedMatch ? fencedMatch[1].trim() : trimmed;

  if (candidate.startsWith("{") && candidate.endsWith("}")) {
    return candidate;
  }

  const firstBrace = candidate.indexOf("{");
  const lastBrace = candidate.lastIndexOf("}");

  if (firstBrace >= 0 && lastBrace > firstBrace) {
    return candidate.slice(firstBrace, lastBrace + 1);
  }

  return "";
};

const parseAssistantJsonObject = (value = "") => {
  const jsonText = extractAssistantJsonObjectText(value);

  if (!jsonText) {
    return null;
  }

  try {
    const parsed = JSON.parse(jsonText);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const extractInlineAssistantAnswer = (value = "") => {
  const safeValue = String(value || "").replace(/\r/g, "").trim();

  if (!safeValue) {
    return "";
  }

  const parsedObject = parseAssistantJsonObject(safeValue);

  if (parsedObject) {
    const parsedAnswer = toSafeText(
      parsedObject?.answer || parsedObject?.message || parsedObject?.reply || "",
      2200
    );

    if (parsedAnswer) {
      return parsedAnswer;
    }
  }

  const inlineJsonFieldPattern =
    /["']?(?:answer|respuesta|reply)["']?\s*[:=-]\s*(["'])([\s\S]*?)\1(?=\s*(?:,\s*["']?(?:suggestions?|visibleProductIds?|products?|action|message|reply)["']?\s*[:=-]|\}|$))/i;
  const inlineJsonFieldMatch = safeValue.match(inlineJsonFieldPattern);

  if (inlineJsonFieldMatch?.[2]) {
    return toSafeText(inlineJsonFieldMatch[2], 2200);
  }

  const inlineLooseFieldPattern =
    /(?:^|[{\n,])\s*["']?(?:answer|respuesta|reply)["']?\s*[:=-]\s*([\s\S]*?)(?=(?:,\s*["']?(?:suggestions?|visibleProductIds?|products?|action|message|reply)["']?\s*[:=-]|\n\s*["']?(?:suggestions?|visibleProductIds?|products?|action|message|reply)["']?\s*[:=-]|\}|$))/i;
  const inlineLooseFieldMatch = safeValue.match(inlineLooseFieldPattern);

  return toSafeText(inlineLooseFieldMatch?.[1] || safeValue, 2200);
};

const normalizeAssistantAnswerText = (value) =>
  stripLeadingAssistantCodeFragments(extractInlineAssistantAnswer(value))
    .replace(/\r/g, "")
    .replace(
      /(?:^|\n)\s*["']?(?:answer|respuesta|reply)["']?\s*[:=-]\s*/i,
      ""
    )
    .replace(/^(?:answer|respuesta|reply)\b[:-]?\s*/i, "")
    .replace(/\b(?:productId|cartItemId|orderId|paymentId)\s*:\s*[\w.-]+/gi, "")
    .replace(/\b(?:qr-order|payment|order)-[\w-]+/gi, "")
    .replace(/\bCPT0[0-9A-Z-]*\b/gi, "")
    .replace(/^(?:[a-z]{1,4}\d{1,6}|\d{2,6}[a-z]{1,4}|[a-z]\d[a-z\d]*)\s+/i, "")
    .replace(/\s+\|\s+/g, " ")
    .replace(/\(\s*\)/g, "")
    .replace(/^["'`[{(]+/, "")
    .replace(/["'`\]})]+$/, "")
    .replace(/\s+/g, " ")
    .trim();

const looksLikeEnglishAssistantReply = (value = "") => {
  const normalized = normalizeSearchText(value);

  if (!normalized || normalized.length < 12) {
    return false;
  }

  const englishSignals = [
    "what",
    "would",
    "with",
    "available",
    "choose",
    "drink",
    "food",
    "here",
    "there",
    "your",
    "you can",
    "i can",
    "option",
    "options",
    "price",
    "hello",
  ];
  const spanishSignals = [
    "tenes",
    "tienes",
    "puedo",
    "quieres",
    "te",
    "para",
    "con",
    "sin",
    "pesos",
    "menu",
    "plato",
    "bebida",
    "opciones",
  ];
  const englishHits = englishSignals.reduce(
    (total, token) => total + (normalized.includes(token) ? 1 : 0),
    0
  );
  const spanishHits = spanishSignals.reduce(
    (total, token) => total + (normalized.includes(token) ? 1 : 0),
    0
  );

  return englishHits >= 2 && englishHits > spanishHits;
};

const looksLikeGarbageAssistantReply = (value = "") => {
  const normalized = normalizeSearchText(value);

  if (!normalized) {
    return true;
  }

  const technicalSignals = [
    "visibleproductids",
    "productid",
    "cartitemid",
    "paymentid",
    "confidence",
    "suggestions",
    "action",
    "reply",
    "json",
  ];
  const technicalHits = technicalSignals.reduce(
    (total, token) => total + (normalized.includes(token) ? 1 : 0),
    0
  );

  if (technicalHits >= 2) {
    return true;
  }

  const tokens = normalized.match(/[a-z0-9áéíóúüñ]+/gi) || [];

  if (!tokens.length) {
    return true;
  }

  const meaningfulTokens = tokens.filter(
    (token) => token.replace(/[0-9]/g, "").length >= 3
  );

  if (meaningfulTokens.length === 0) {
    return true;
  }

  if (/^(?:[a-z0-9]{1,2}\s+){2,10}[a-z0-9]{1,2}$/i.test(normalized)) {
    return true;
  }

  return false;
};

const normalizeAssistantAction = (value = null) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const allowedTypes = new Set([
    "none",
    "show_products",
    "open_cart",
    "open_product",
    "add",
    "remove",
    "replace",
    "update_quantity",
    "call_waiter",
    "clarify",
  ]);
  const type = normalizeSearchText(value?.type || value?.action);

  if (!allowedTypes.has(type)) {
    return null;
  }

  return {
    type,
    productId: toSafeText(
      value?.productId ||
        value?.product_id ||
        value?.targetProductId ||
        value?.target_product_id,
      120
    ),
    targetProductId: toSafeText(
      value?.targetProductId || value?.target_product_id,
      120
    ),
    cartItemId: toSafeText(
      value?.cartItemId ||
        value?.cart_item_id ||
        value?.sourceCartItemId ||
        value?.source_cart_item_id,
      160
    ),
    quantity: Math.max(1, Math.min(20, Number(value?.quantity || value?.qty || 1) || 1)),
    confidence: Math.max(0, Math.min(1, Number(value?.confidence || 0) || 0)),
    reason: toSafeText(value?.reason, 220),
  };
};

const extractFunctionErrorPayload = async (error, data) => {
  if (data && typeof data === "object") {
    return data;
  }

  const response = error?.context;

  if (!response) {
    return null;
  }

  const readableResponse =
    typeof response.clone === "function" ? response.clone() : response;

  try {
    const contentType = toSafeText(
      readableResponse.headers?.get?.("content-type"),
      120
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      return await readableResponse.json();
    }

    const rawText = toSafeText(await readableResponse.text(), 500);

    if (!rawText) {
      return null;
    }

    try {
      return JSON.parse(rawText);
    } catch {
      return { error: rawText };
    }
  } catch {
    return null;
  }
};

const normalizeFunctionError = (error, data, fallbackMessage) => {
  const transportMessage = toSafeText(error?.message, 240);
  const backendMessage = toSafeText(
    data?.error || data?.message || data?.details || data?.hint,
    240
  );

  if (backendMessage && transportMessage.toLowerCase().includes("non-2xx")) {
    return backendMessage;
  }

  return backendMessage || transportMessage || fallbackMessage;
};

const summarizeCartItem = (item = {}) => {
  const quantity = Math.max(1, Number(item?.quantity || 1));
  const unitPrice = toMoney(item?.unitPrice ?? item?.basePrice);
  const totalPrice = toMoney(item?.totalPrice ?? unitPrice * quantity);
  const selectionDetails = Array.isArray(item?.selectionDetails)
    ? item.selectionDetails
        .map((detail) => toSafeText(detail, 120))
        .filter(Boolean)
        .slice(0, 6)
    : [];

  return {
    id: toSafeText(item?.id || "", 160),
    productId: toSafeText(item?.productId || "", 120),
    productName: toSafeText(item?.productName || item?.name || "Producto", 120),
    quantity,
    unitPrice,
    totalPrice,
    selectionDetails,
  };
};

const summarizeSubmittedOrder = (submittedOrderSnapshot = null) => {
  if (!submittedOrderSnapshot || typeof submittedOrderSnapshot !== "object") {
    return null;
  }

  const items = Array.isArray(submittedOrderSnapshot?.items)
    ? submittedOrderSnapshot.items
        .map((item) => ({
          productName: toSafeText(
            item?.productName || item?.name || "Producto",
            120
          ),
          quantity: Math.max(1, Number(item?.quantity || 1)),
          selectionDetails: Array.isArray(item?.selectionDetails)
            ? item.selectionDetails
                .map((detail) => toSafeText(detail, 120))
                .filter(Boolean)
                .slice(0, 6)
            : [],
        }))
        .slice(0, 12)
    : [];

  return {
    orderId: toSafeText(submittedOrderSnapshot?.orderId || "", 160),
    preparationNumber: Number(submittedOrderSnapshot?.preparationNumber || 0) || null,
    checkoutMethod: toSafeText(submittedOrderSnapshot?.checkoutMethod || "", 80),
    paymentStatus: toSafeText(submittedOrderSnapshot?.paymentStatus || "", 80),
    awaitingPaymentApproval: submittedOrderSnapshot?.awaitingPaymentApproval === true,
    totalAmount: toMoney(
      submittedOrderSnapshot?.pricing?.finalTotal ||
        submittedOrderSnapshot?.pricing?.listTotal ||
        submittedOrderSnapshot?.totalAmount
    ),
    items,
  };
};

const summarizeConversation = (conversation = []) =>
  (Array.isArray(conversation) ? conversation : [])
    .map((entry, index) => ({
      id: toSafeText(
        entry?.id || `msg-${index + 1}`,
        80
      ),
      role:
        String(entry?.role || "").trim().toLowerCase() === "assistant"
          ? "assistant"
          : "user",
      text: toSafeText(entry?.text || entry?.content || "", 600),
    }))
    .filter((entry) => entry.text)
    .slice(-12);

const getProductIngredients = (product = {}) =>
  (Array.isArray(product?.ingredients) ? product.ingredients : [])
    .map((ingredient) => toSafeText(ingredient?.name || ingredient?.nombre, 80))
    .filter(Boolean);

const getProductSearchHaystack = (product = {}) =>
  normalizeSearchText(
    [
      product?.name,
      product?.description,
      product?.subcategoryName,
      product?.categoryName,
      ...getProductIngredients(product),
    ]
      .filter(Boolean)
      .join(" ")
  );

const getProductCategoryId = (product = {}) =>
  toSafeText(product?.categoryId || product?.category_id || product?.categoria, 120);

const getProductSubcategoryId = (product = {}) =>
  toSafeText(
    product?.subcategoryId ||
      product?.subcategory_id ||
      product?.subcategoriaId ||
      product?.subcategoria_id,
    120
  );

const getProductCategoryName = (product = {}) =>
  toSafeText(product?.categoryName || product?.categoria || "Categoria", 120);

const getProductSubcategoryName = (product = {}) =>
  toSafeText(product?.subcategoryName || product?.subcategoria || "", 120);

const getProductName = (product = {}) =>
  sanitizeAssistantDisplayName(product?.name || product?.nombre || "", "Producto");

const getLinkedRuleSourceSubcategoryId = (rule = {}) =>
  toSafeText(rule?.sourceSubcategoryId || rule?.source_subcategory_id, 120);

const getLinkedRuleTargetCategoryId = (rule = {}) =>
  toSafeText(rule?.targetCategoryId || rule?.target_category_id, 120);

const getLinkedRuleTargetSubcategoryId = (rule = {}) =>
  toSafeText(rule?.targetSubcategoryId || rule?.target_subcategory_id, 120);

const getLinkedRuleGroupLabel = (rule = {}) =>
  toSafeText(
    rule?.title ||
      rule?.targetSubcategoryName ||
      rule?.target_subcategory_name ||
      rule?.targetCategoryName ||
      rule?.target_category_name ||
      "acompanamiento",
    120
  );

const MENU_PAIRING_RULES = [
  {
    sourceLabel: "pastas",
    optionLabel: "salsas",
    sourcePattern:
      /\b(pasta|pastas|raviol|ravioles|noqui|noquis|tallarin|tallarines|sorrentino|spaghetti|espagueti)\b/,
    optionPattern:
      /\b(salsa|salsas|fileto|bolognesa|bolonesa|crema|tuco|scarparo|parisien|parmesano|cuatro quesos|queso)\b/,
  },
  {
    sourceLabel: "milanesas",
    optionLabel: "guarniciones",
    sourcePattern: /\b(mila|milanesa|milanesas|suprema|supremas)\b/,
    optionPattern:
      /\b(papa|papas|frita|fritas|pure|pures|ensalada|ensaladas|guarnicion|guarniciones)\b/,
  },
  {
    sourceLabel: "hamburguesas",
    optionLabel: "extras o acompanamientos",
    sourcePattern: /\b(hamburguesa|hamburguesas|burger|burgers|lomito|sandwich|sanguche)\b/,
    optionPattern: /\b(papa|papas|frita|fritas|bebida|gaseosa|agua|jugo|extra|cheddar|bacon|panceta)\b/,
  },
  {
    sourceLabel: "carnes",
    optionLabel: "guarniciones",
    sourcePattern: /\b(carne|bife|churrasco|parrilla|asado|pollo)\b/,
    optionPattern:
      /\b(papa|papas|frita|fritas|pure|pures|ensalada|ensaladas|guarnicion|guarniciones)\b/,
  },
  {
    sourceLabel: "pizzas",
    optionLabel: "bebidas",
    sourcePattern: /\b(pizza|pizzas|muzza|muzarella|mozzarella|fugazza|napolitana)\b/,
    optionPattern: /\b(bebida|bebidas|gaseosa|gaseosas|agua|jugo|cerveza|vino)\b/,
  },
];

const getQuestionMainDishRule = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  return (
    MENU_PAIRING_RULES.find((rule) => rule.sourcePattern.test(normalizedQuestion)) ||
    null
  );
};

const productMatchesPattern = (product = {}, pattern) =>
  Boolean(pattern && pattern.test(getProductSearchHaystack(product)));

const DRINK_PRODUCT_PATTERN =
  /\b(bebida|bebidas|gaseosa|gaseosas|agua|aguas|jugo|jugos|cerveza|cervezas|vino|vinos|trago|tragos|licuado|licuados|soda|sodas|cafe|cafes|te|tes|infusion|infusiones|leche|coca|pepsi|sprite|fanta|seven up|7up)\b/;

const FOOD_QUESTION_PATTERN =
  /\b(comer|comida|comidas|plato|platos|almorzar|almuerzo|cenar|cena|salado|salada)\b/;

const FOOD_PRODUCT_NAME_PATTERN =
  /\b(combo|menu|promocion|milanesa|milanesas|suprema|supremas|hamburguesa|hamburguesas|burger|burgers|pizza|pizzas|pasta|pastas|raviol|ravioles|noqui|noquis|tallarin|tallarines|lomito|lomitos|sandwich|sandwiches|sanguche|carne|pollo|papa|papas|pure|pures|ensalada|ensaladas|medialuna|medialunas|tostada|tostadas|tostado|tostados|postre|postres|helado|helados|torta|tortas|flan)\b/;

const DRINK_EXCLUSION_PATTERN =
  /\b(?:no|sin)\s+(?:quiero\s+)?(?:bebida|bebidas|tomar|beber)|\bcomida\s+(?:y\s+)?no\s+(?:bebida|bebidas)\b/;

const isDrinkFamilyQuestion = (question = "") =>
  /\b(bebida|bebidas|tomar|para tomar|beber|gaseosa|gaseosas|agua|aguas|jugo|jugos|cerveza|cervezas|vino|vinos|trago|tragos|cafe|cafes)\b/.test(
    normalizeSearchText(question)
  );

const isFoodFamilyQuestion = (question = "") =>
  FOOD_QUESTION_PATTERN.test(normalizeSearchText(question));

const isFoodOnlyQuestion = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);
  return (
    isFoodFamilyQuestion(normalizedQuestion) &&
    (!isDrinkFamilyQuestion(normalizedQuestion) ||
      DRINK_EXCLUSION_PATTERN.test(normalizedQuestion))
  );
};

const isBroadFoodListQuestion = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  if (!isFoodOnlyQuestion(normalizedQuestion)) {
    return false;
  }

  return (
    /\bque\b.*\b(?:tenes|tiene|tienen|hay|ofrecen|venden)\b.*\b(?:comer|comida|comidas|plato|platos)\b/.test(
      normalizedQuestion
    ) ||
    /\b(?:que|cuales)\s+platos\b/.test(normalizedQuestion) ||
    /\b(?:opciones|lista|mostrame|muestrame|mostrar)\b.*\b(?:comer|comida|comidas|plato|platos)\b/.test(
      normalizedQuestion
    )
  );
};

const buildBroadFoodListAnswer = (products = []) => {
  const names = (Array.isArray(products) ? products : [])
    .map((product) => toSafeText(product?.name || "", 120))
    .filter(Boolean)
    .slice(0, 8);

  if (!names.length) {
    return "Ahora no veo platos disponibles para comer en esta carta.";
  }

  return `Para comer tenes ${joinNaturalOptions(names, "y")}. Te muestro solo opciones de comida para que elijas.`;
};

const isDrinkProduct = (product = {}) => {
  const name = normalizeSearchText(product?.name || product?.nombre || "");
  const structuralText = normalizeSearchText(
    [
      name,
      product?.categoryName || product?.categoria,
      product?.subcategoryName || product?.subcategoria,
    ]
      .filter(Boolean)
      .join(" ")
  );

  if (FOOD_PRODUCT_NAME_PATTERN.test(name)) {
    return false;
  }

  return DRINK_PRODUCT_PATTERN.test(structuralText);
};

export const filterPublicMenuAssistantProductsForQuestion = (
  question = "",
  products = []
) => {
  const normalizedProducts = (Array.isArray(products) ? products : []).filter(isAssistantStandaloneProduct);

  if (isFoodOnlyQuestion(question)) {
    return normalizedProducts.filter((product) => !isDrinkProduct(product));
  }

  if (
    isDrinkFamilyQuestion(question) &&
    !isFoodFamilyQuestion(question) &&
    !DRINK_EXCLUSION_PATTERN.test(normalizeSearchText(question))
  ) {
    return normalizedProducts.filter((product) => isDrinkProduct(product));
  }

  return normalizedProducts;
};

const scoreProductForStructuralQuestion = (question = "", product = {}) => {
  const normalizedQuestion = normalizeSearchText(question);
  const terms = expandQueryTokens(question);
  const name = normalizeSearchText(product?.name || product?.nombre || "");
  const category = normalizeSearchText(product?.categoryName || product?.categoria || "");
  const subcategory = normalizeSearchText(
    product?.subcategoryName || product?.subcategoria || ""
  );
  const structuralText = [name, category, subcategory].filter(Boolean).join(" ");
  let score = 0;

  if (!structuralText) {
    return 0;
  }

  if (name && normalizedQuestion.includes(name)) {
    score += 30;
  }

  if (subcategory && normalizedQuestion.includes(subcategory)) {
    score += 24;
  }

  if (category && normalizedQuestion.includes(category)) {
    score += 22;
  }

  terms.forEach((term) => {
    if (name.includes(term)) {
      score += 12;
    } else if (subcategory.includes(term)) {
      score += 10;
    } else if (category.includes(term)) {
      score += 9;
    } else if (structuralText.includes(term)) {
      score += 4;
    }
  });

  return score;
};

const getStrictListProductsForQuestion = (
  question = "",
  products = [],
  { limit = 10 } = {}
) => {
  const normalizedProducts = filterPublicMenuAssistantProductsForQuestion(
    question,
    products
  );
  const foodFamilyMatches = isFoodOnlyQuestion(question)
    ? normalizedProducts.map((product, index) => ({
        product,
        index,
        price: getProductPrice(product),
      }))
    : [];

  if (foodFamilyMatches.length > 0) {
    return foodFamilyMatches
      .sort((left, right) => left.index - right.index)
      .slice(0, limit)
      .map((entry) => entry.product);
  }

  const drinkFamilyMatches = isDrinkFamilyQuestion(question)
    ? normalizedProducts
        .map((product, index) => ({
          product,
          index,
          price: getProductPrice(product),
        }))
        .filter((entry) => isDrinkProduct(entry.product))
    : [];

  if (drinkFamilyMatches.length > 0) {
    return drinkFamilyMatches
      .sort((left, right) => left.index - right.index)
      .slice(0, limit)
      .map((entry) => entry.product);
  }

  const structuralMatches = normalizedProducts
    .map((product, index) => ({
      product,
      index,
      score: scoreProductForStructuralQuestion(question, product),
      price: getProductPrice(product),
    }))
    .filter((entry) => entry.score > 0);

  if (structuralMatches.length > 0) {
    return structuralMatches
      .sort((left, right) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }

        return right.price - left.price || left.index - right.index;
      })
      .slice(0, limit)
      .map((entry) => entry.product);
  }

  const mainDishRule = getQuestionMainDishRule(question);
  const rankedMatches = normalizedProducts
    .map((product, index) => ({
      product,
      index,
      score: scoreProductForQuestion(question, product),
      haystack: getProductSearchHaystack(product),
      price: getProductPrice(product),
    }))
    .filter((entry) => entry.score > 0);

  const strictMatches = mainDishRule
    ? rankedMatches.filter(
        (entry) =>
          mainDishRule.sourcePattern.test(entry.haystack) &&
          !mainDishRule.optionPattern.test(entry.haystack)
      )
    : [];
  const sourceMatches = mainDishRule
    ? rankedMatches.filter((entry) => mainDishRule.sourcePattern.test(entry.haystack))
    : [];
  const finalMatches =
    strictMatches.length > 0
      ? strictMatches
      : sourceMatches.length > 0
        ? sourceMatches
        : rankedMatches;

  return finalMatches
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      return right.price - left.price || left.index - right.index;
    })
    .slice(0, limit)
    .map((entry) => entry.product);
};

const MAIN_DISH_PRODUCT_NAME_PATTERN =
  /\b(milanesa|milanesas|suprema|supremas|hamburguesa|hamburguesas|burger|burgers|pizza|pizzas|pasta|pastas|ravioles|noquis|tallarines|lomito|lomitos|sandwich|sandwiches|carne|pollo)\b/;

const ACCOMPANIMENT_PRODUCT_PATTERN =
  /\b(salsa|salsas|papa|papas|frita|fritas|pure|pures|ensalada|ensaladas|guarnicion|guarniciones|bebida|gaseosa|agua|jugo|aderezo)\b/;

const isLikelyAccompanimentProduct = (product = {}) => {
  const name = normalizeSearchText(product?.name || product?.nombre || "");

  if (MAIN_DISH_PRODUCT_NAME_PATTERN.test(name)) {
    return false;
  }

  return ACCOMPANIMENT_PRODUCT_PATTERN.test(getProductSearchHaystack(product));
};

const resolveExplicitLinkedProducts = (rule = {}, products = [], sourceProduct = {}) => {
  const sourceProductId = toSafeText(sourceProduct?.id || sourceProduct?.productId, 120);
  return resolveLinkedRuleProducts(rule, products, sourceProductId);
};

const buildMenuPairingContexts = ({
  question = "",
  products = [],
  linkedRules = [],
  sourceProducts = [],
  limit = 4,
} = {}) => {
  const { products: normalizedProducts, standaloneProducts } = getAssistantMenuCatalog(products, linkedRules);
  const sourceCandidates = (Array.isArray(sourceProducts) && sourceProducts.length > 0
    ? sourceProducts
    : rankProductsForQuestion(question, standaloneProducts, { limit: 8 })
  ).filter(isAssistantStandaloneProduct);
  const contexts = [];
  const seenKeys = new Set();

  sourceCandidates.forEach((sourceProduct) => {
    (Array.isArray(linkedRules) ? linkedRules : []).forEach((rule) => {
      if (!matchesLinkedRule(rule, sourceProduct)) {
        return;
      }

      const options = resolveExplicitLinkedProducts(rule, normalizedProducts, sourceProduct)
        .slice(0, 5);

      if (!options.length) {
        return;
      }

      const key = `${toSafeText(sourceProduct?.id || sourceProduct?.productId, 120)}::${getLinkedRuleGroupLabel(rule)}`;
      if (seenKeys.has(key)) {
        return;
      }

      seenKeys.add(key);
      contexts.push({
        source: sourceProduct,
        label: getLinkedRuleGroupLabel(rule),
        options,
        explicit: true,
      });
    });
  });

  sourceCandidates.forEach((sourceProduct) => {
    const matchingRule = MENU_PAIRING_RULES.find((rule) =>
      productMatchesPattern(sourceProduct, rule.sourcePattern)
    );

    if (!matchingRule) {
      return;
    }

    const options = standaloneProducts
      .filter(
        (product) =>
          toSafeText(product?.id || product?.productId, 120) !==
            toSafeText(sourceProduct?.id || sourceProduct?.productId, 120) &&
          productMatchesPattern(product, matchingRule.optionPattern)
      )
      .sort((left, right) => {
        const leftQuestionScore = scoreProductForQuestion(question, left);
        const rightQuestionScore = scoreProductForQuestion(question, right);
        if (rightQuestionScore !== leftQuestionScore) {
          return rightQuestionScore - leftQuestionScore;
        }

        return getProductPrice(left) - getProductPrice(right);
      })
      .slice(0, 5);

    if (!options.length) {
      return;
    }

    const key = `${toSafeText(sourceProduct?.id || sourceProduct?.productId, 120)}::${matchingRule.optionLabel}`;
    if (seenKeys.has(key)) {
      return;
    }

    seenKeys.add(key);
    contexts.push({
      source: sourceProduct,
      label: matchingRule.optionLabel,
      options,
      explicit: false,
    });
  });

  return contexts.slice(0, limit);
};

const formatPairingContexts = (contexts = []) => {
  const normalizedContexts = Array.isArray(contexts) ? contexts : [];

  if (!normalizedContexts.length) {
    return "";
  }

  const groupedContexts = new Map();

  normalizedContexts.forEach((context) => {
    const sourceName = getProductName(context.source);
    const sourceVariant = splitProductVariantName(sourceName);
    const sourceLabel = sourceVariant?.baseName || sourceName;
    const sourceKey = normalizeSearchText(sourceLabel);
    const optionNames = compactSimilarOptionNames(
      (context.options || [])
        .map((product) => getProductName(product))
        .filter(Boolean)
        .slice(0, 6)
    );

    if (!sourceKey || !sourceLabel || !optionNames.length) {
      return;
    }

    if (!groupedContexts.has(sourceKey)) {
      groupedContexts.set(sourceKey, {
        sourceLabel,
        groups: [],
        seenLabels: new Set(),
      });
    }

    const groupedSource = groupedContexts.get(sourceKey);
    const groupLabel = toSafeText(context.label || "acompanamientos", 120);
    const labelKey = normalizeSearchText(groupLabel);

    if (!labelKey || groupedSource.seenLabels.has(labelKey)) {
      return;
    }

    groupedSource.seenLabels.add(labelKey);
    groupedSource.groups.push(
      `${groupLabel}: ${joinNaturalOptions(optionNames, "o")}`
    );
  });

  return Array.from(groupedContexts.values())
    .map((entry) =>
      entry.groups.length > 0
        ? `${entry.sourceLabel} combina con ${entry.groups.join(". ")}`
        : ""
    )
    .filter(Boolean)
    .join(". ");
};

const splitProductVariantName = (name = "") => {
  const safeName = toSafeText(name, 160);
  const match = safeName.match(
    /^(.+?)\s+(?:con|c\/|c\s*\/|acompanad[oa]\s+con|acompañad[oa]\s+con)\s+(.+)$/i
  );

  if (!match) {
    return null;
  }

  const baseName = toSafeText(match[1], 120);
  const optionName = toSafeText(match[2], 120);

  if (baseName.length < 4 || optionName.length < 3) {
    return null;
  }

  return {
    baseName,
    optionName,
  };
};

const buildDistinctBaseProductTitles = (products = []) =>
  Array.from(
    new Map(
      (Array.isArray(products) ? products : [])
        .map((product) => {
          const productName = getProductName(product);
          const variant = splitProductVariantName(productName);

          return [
            normalizeSearchText(variant?.baseName || productName),
            variant?.baseName || productName,
          ];
        })
        .filter(([key, value]) => key && value)
    ).values()
  );

const joinNaturalOptions = (items = [], conjunction = "o") => {
  const normalizedItems = (Array.isArray(items) ? items : [])
    .map((item) => toSafeText(item, 120))
    .filter(Boolean);

  if (normalizedItems.length <= 1) {
    return normalizedItems[0] || "";
  }

  if (normalizedItems.length === 2) {
    return `${normalizedItems[0]} ${conjunction} ${normalizedItems[1]}`;
  }

  return `${normalizedItems.slice(0, -1).join(", ")} ${conjunction} ${
    normalizedItems[normalizedItems.length - 1]
  }`;
};

const compactSimilarOptionNames = (optionNames = []) => {
  const orderedNames = [];
  const groupsByPrefix = new Map();
  const usedNormalizedNames = new Set();

  (Array.isArray(optionNames) ? optionNames : []).forEach((optionName) => {
    const safeName = toSafeText(optionName, 120);
    const normalizedName = normalizeSearchText(safeName);

    if (!safeName || usedNormalizedNames.has(normalizedName)) {
      return;
    }

    usedNormalizedNames.add(normalizedName);
    orderedNames.push(safeName);

    const match = safeName.match(/^(.+?)\s+de\s+(.+)$/i);
    if (!match) {
      return;
    }

    const prefix = toSafeText(match[1], 80);
    const tail = toSafeText(match[2], 80);
    const prefixKey = normalizeSearchText(prefix);

    if (!prefixKey || !tail) {
      return;
    }

    if (!groupsByPrefix.has(prefixKey)) {
      groupsByPrefix.set(prefixKey, {
        prefix,
        names: [],
        tails: [],
      });
    }

    const group = groupsByPrefix.get(prefixKey);
    group.names.push(safeName);
    group.tails.push(tail);
  });

  const consumedNames = new Set();
  const compactNames = [];

  groupsByPrefix.forEach((group) => {
    if (group.names.length < 2) {
      return;
    }

    group.names.forEach((name) => consumedNames.add(normalizeSearchText(name)));
    const compactTail =
      group.tails.length === 2
        ? `${group.tails[0]} o de ${group.tails[1]}`
        : `${group.tails.slice(0, -1).join(", de ")} o de ${
            group.tails[group.tails.length - 1]
          }`;
    compactNames.push(`${group.prefix} de ${compactTail}`);
  });

  orderedNames.forEach((name) => {
    if (!consumedNames.has(normalizeSearchText(name))) {
      compactNames.push(name);
    }
  });

  return compactNames;
};

const buildProductVariantSummary = (products = []) => {
  const groupsByBase = new Map();
  const standaloneNames = [];

  (Array.isArray(products) ? products : []).forEach((product) => {
    const productName = getProductName(product);
    const variant = splitProductVariantName(productName);

    if (!variant) {
      standaloneNames.push(productName);
      return;
    }

    const baseKey = normalizeSearchText(variant.baseName);
    if (!groupsByBase.has(baseKey)) {
      groupsByBase.set(baseKey, {
        baseName: variant.baseName,
        options: [],
      });
    }

    groupsByBase.get(baseKey).options.push(variant.optionName);
  });

  const titles = [];
  const variantPairings = [];

  groupsByBase.forEach((group) => {
    const compactOptions = compactSimilarOptionNames(group.options);

    if (compactOptions.length >= 2) {
      titles.push(group.baseName);
      variantPairings.push(
        `${group.baseName} se puede pedir con ${joinNaturalOptions(
          compactOptions,
          "o"
        )}`
      );
      return;
    }

    titles.push(
      group.options[0] ? `${group.baseName} con ${group.options[0]}` : group.baseName
    );
  });

  standaloneNames.forEach((name) => {
    if (name) {
      titles.push(name);
    }
  });

  return {
    titles: Array.from(
      new Map(titles.map((title) => [normalizeSearchText(title), title])).values()
    ),
    variantPairings,
  };
};

const buildMenuHierarchy = (products = []) => {
  const categoryMap = new Map();
  const subcategoryMap = new Map();

  (Array.isArray(products) ? products : []).forEach((product) => {
    const categoryId =
      getProductCategoryId(product) ||
      `category:${normalizeSearchText(getProductCategoryName(product))}`;
    const categoryName = getProductCategoryName(product) || "Categoria";
    const subcategoryId =
      getProductSubcategoryId(product) ||
      `subcategory:${normalizeSearchText(getProductSubcategoryName(product))}`;
    const subcategoryName = getProductSubcategoryName(product);

    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, {
        categoryId,
        categoryName,
        products: [],
        subcategories: [],
      });
    }

    const categoryEntry = categoryMap.get(categoryId);
    categoryEntry.products.push(product);

    if (!subcategoryName) {
      return;
    }

    if (!subcategoryMap.has(subcategoryId)) {
      const subcategoryEntry = {
        subcategoryId,
        subcategoryName,
        categoryId,
        categoryName,
        products: [],
      };
      subcategoryMap.set(subcategoryId, subcategoryEntry);
      categoryEntry.subcategories.push(subcategoryEntry);
    }

    subcategoryMap.get(subcategoryId).products.push(product);
  });

  return {
    categories: Array.from(categoryMap.values()),
    subcategories: Array.from(subcategoryMap.values()),
    categoryMap,
    subcategoryMap,
  };
};

const countProductsByHierarchyKey = (
  matchedProducts = [],
  keyResolver = () => ""
) => {
  const counts = new Map();

  (Array.isArray(matchedProducts) ? matchedProducts : []).forEach((product) => {
    const key = toSafeText(keyResolver(product), 160);

    if (!key) {
      return;
    }

    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return counts;
};

const resolveHierarchyFocus = ({
  question = "",
  hierarchy,
  matchedProducts = [],
} = {}) => {
  if (!hierarchy) {
    return null;
  }

  const normalizedQuestion = normalizeSearchText(question);
  const terms = tokenize(question);
  const matchedCategoryCounts = countProductsByHierarchyKey(
    matchedProducts,
    getProductCategoryId
  );
  const matchedSubcategoryCounts = countProductsByHierarchyKey(
    matchedProducts,
    getProductSubcategoryId
  );

  let bestCategory = null;
  let bestCategoryScore = 0;
  hierarchy.categories.forEach((categoryEntry) => {
    const normalizedCategoryName = normalizeSearchText(categoryEntry.categoryName);
    const categorySubcategoryNames = (categoryEntry.subcategories || []).map(
      (subcategoryEntry) => subcategoryEntry.subcategoryName
    );
    let score = 0;

    if (normalizedCategoryName && normalizedQuestion.includes(normalizedCategoryName)) {
      score += 34;
    }

    if (
      isDrinkFamilyQuestion(question) &&
      DRINK_PRODUCT_PATTERN.test(normalizedCategoryName)
    ) {
      score += 34;
    }

    terms.forEach((term) => {
      if (normalizedCategoryName.includes(term)) {
        score += 9;
      } else if (
        categoryEntry.subcategories.some((subcategoryEntry) =>
          normalizeSearchText(subcategoryEntry.subcategoryName).includes(term)
        )
      ) {
        score += 4;
      }
    });

    const ontologyOverlap = scoreOntologyFamilyOverlap(question, [
      categoryEntry.categoryName,
      ...categorySubcategoryNames,
    ]);
    score += ontologyOverlap * 15;

    score += (matchedCategoryCounts.get(categoryEntry.categoryId) || 0) * 8;

    if (score > bestCategoryScore) {
      bestCategory = categoryEntry;
      bestCategoryScore = score;
    }
  });

  let bestSubcategory = null;
  let bestSubcategoryScore = 0;
  hierarchy.subcategories.forEach((subcategoryEntry) => {
    const normalizedSubcategoryName = normalizeSearchText(
      subcategoryEntry.subcategoryName
    );
    let score = 0;

    if (
      normalizedSubcategoryName &&
      normalizedQuestion.includes(normalizedSubcategoryName)
    ) {
      score += 38;
    }

    terms.forEach((term) => {
      if (normalizedSubcategoryName.includes(term)) {
        score += 11;
      }
    });

    score +=
      scoreOntologyFamilyOverlap(question, [
        subcategoryEntry.subcategoryName,
        subcategoryEntry.categoryName,
      ]) * 18;

    score += (matchedSubcategoryCounts.get(subcategoryEntry.subcategoryId) || 0) * 10;

    if (score > bestSubcategoryScore) {
      bestSubcategory = subcategoryEntry;
      bestSubcategoryScore = score;
    }
  });

  if (
    bestSubcategory &&
    bestSubcategoryScore > 0 &&
    bestSubcategoryScore >= bestCategoryScore + 4
  ) {
    return {
      type: "subcategory",
      entry: bestSubcategory,
    };
  }

  if (bestCategory && bestCategoryScore > 0) {
    return {
      type: "category",
      entry: bestCategory,
    };
  }

  const firstMatchedProduct = Array.isArray(matchedProducts) ? matchedProducts[0] : null;
  if (firstMatchedProduct) {
    const fallbackSubcategoryId = getProductSubcategoryId(firstMatchedProduct);
    const fallbackCategoryId = getProductCategoryId(firstMatchedProduct);

    if (fallbackSubcategoryId && hierarchy.subcategoryMap.has(fallbackSubcategoryId)) {
      return {
        type: "subcategory",
        entry: hierarchy.subcategoryMap.get(fallbackSubcategoryId),
      };
    }

    if (fallbackCategoryId && hierarchy.categoryMap.has(fallbackCategoryId)) {
      return {
        type: "category",
        entry: hierarchy.categoryMap.get(fallbackCategoryId),
      };
    }
  }

  return null;
};

const summarizeHierarchyTargetOptions = (products = [], fallbackName = "") => {
  const titles = buildProductVariantSummary(products).titles.slice(0, 6);

  if (titles.length > 0) {
    return titles;
  }

  return fallbackName ? [fallbackName] : [];
};

const buildHierarchyLinkedGroups = ({
  sourceSubcategoryId = "",
  hierarchy,
  linkedRules = [],
  maxDepth = 3,
} = {}) => {
  if (!sourceSubcategoryId || !hierarchy) {
    return [];
  }

  const groups = [];
  const queue = [{ sourceSubcategoryId, depth: 0 }];
  const visitedSources = new Set([sourceSubcategoryId]);
  const seenKeys = new Set();

  while (queue.length > 0) {
    const current = queue.shift();

    (Array.isArray(linkedRules) ? linkedRules : []).forEach((rule) => {
      if (getRuleField(rule, "source", "Product") || rule.active === false ||
          getLinkedRuleSourceSubcategoryId(rule) !== current?.sourceSubcategoryId) {
        return;
      }

      const targetSubcategoryId = getLinkedRuleTargetSubcategoryId(rule);
      const targetCategoryId = getLinkedRuleTargetCategoryId(rule);
      const targetProductId = getRuleField(rule, "target", "Product");
      const label = getLinkedRuleGroupLabel(rule) || "acompanamientos";
      let optionTitles = [];
      let key = "";

      if (targetProductId) {
        const targetProducts = Array.from(hierarchy.categoryMap.values()).flatMap((entry) => entry.products || []);
        const target = targetProducts.find((product) => product.id === targetProductId);
        optionTitles = target ? [target.name || target.nombre] : [];
        key = `product::${targetProductId}`;
      } else if (targetSubcategoryId && hierarchy.subcategoryMap.has(targetSubcategoryId)) {
        const targetSubcategory = hierarchy.subcategoryMap.get(targetSubcategoryId);
        optionTitles = summarizeHierarchyTargetOptions(
          targetSubcategory.products,
          targetSubcategory.subcategoryName
        );
        key = `subcategory::${targetSubcategoryId}`;

        if (
          current.depth + 1 < maxDepth &&
          !visitedSources.has(targetSubcategoryId)
        ) {
          visitedSources.add(targetSubcategoryId);
          queue.push({
            sourceSubcategoryId: targetSubcategoryId,
            depth: current.depth + 1,
          });
        }
      } else if (targetCategoryId && hierarchy.categoryMap.has(targetCategoryId)) {
        const targetCategory = hierarchy.categoryMap.get(targetCategoryId);
        optionTitles =
          targetCategory.subcategories.length > 0
            ? targetCategory.subcategories
                .map((subcategoryEntry) => toSafeText(subcategoryEntry.subcategoryName, 120))
                .filter(Boolean)
                .slice(0, 6)
            : summarizeHierarchyTargetOptions(
                targetCategory.products,
                targetCategory.categoryName
              );
        key = `category::${targetCategoryId}`;
      }

      if (!key || !optionTitles.length) {
        return;
      }

      const dedupeKey = `${label}::${key}`;
      if (seenKeys.has(dedupeKey)) {
        return;
      }

      seenKeys.add(dedupeKey);
      groups.push({
        label,
        optionTitles: compactSimilarOptionNames(optionTitles).slice(0, 6),
      });
    });
  }

  return groups;
};

const formatHierarchyLinkedGroups = (groups = []) =>
  (Array.isArray(groups) ? groups : [])
    .map((group) => {
      const safeLabel = toSafeText(group?.label || "acompanamientos", 120);
      const optionTitles = compactSimilarOptionNames(group?.optionTitles || []).slice(0, 6);

      if (!optionTitles.length) {
        return "";
      }

      return `${safeLabel}: ${joinNaturalOptions(optionTitles, "o")}.`;
    })
    .filter(Boolean)
    .join(" ");

const summarizePairingProducts = (contexts = []) =>
  (Array.isArray(contexts) ? contexts : []).flatMap((context) => [
    context.source,
    ...(Array.isArray(context.options) ? context.options : []),
  ]);

const scoreProductForQuestion = (question, product = {}) => {
  const normalizedQuestion = normalizeSearchText(question);
  const terms = expandQueryTokens(question);
  const name = normalizeSearchText(product?.name || "");
  const description = normalizeSearchText(product?.description || "");
  const category = normalizeSearchText(product?.categoryName || "");
  const subcategory = normalizeSearchText(product?.subcategoryName || "");
  const ingredients = normalizeSearchText(getProductIngredients(product).join(" "));
  const haystack = [name, description, category, subcategory, ingredients]
    .filter(Boolean)
    .join(" ");
  let score = 0;

  if (!haystack) {
    return 0;
  }

  if (name && normalizedQuestion.includes(name)) {
    score += 24;
  }

  if (subcategory && normalizedQuestion.includes(subcategory)) {
    score += 14;
  }

  if (category && normalizedQuestion.includes(category)) {
    score += 10;
  }

  score +=
    scoreOntologyFamilyOverlap(question, [
      product?.name,
      product?.categoryName,
      product?.subcategoryName,
      product?.description,
    ]) * 8;

  terms.forEach((term) => {
    if (name.includes(term)) {
      score += 10;
    } else if (subcategory.includes(term)) {
      score += 8;
    } else if (category.includes(term)) {
      score += 7;
    } else if (ingredients.includes(term)) {
      score += 5;
    } else if (description.includes(term)) {
      score += 3;
    } else if (haystack.includes(term)) {
      score += 2;
    }
  });

  return score;
};

const findMatchingProducts = (question, products = []) => {
  return (Array.isArray(products) ? products : [])
    .map((product) => {
      return {
        product,
        score: scoreProductForQuestion(question, product),
      };
    })
    .filter((entry) => entry.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      return (
        toMoney(right.product?.price ?? right.product?.suggestedPrice) -
        toMoney(left.product?.price ?? left.product?.suggestedPrice)
      );
    })
    .slice(0, 8)
    .map((entry) => entry.product);
};

const getProductPrice = (product = {}) =>
  toMoney(product?.price ?? product?.suggestedPrice);

const isDrinkQuestion = (question) =>
  /(bebida|bebidas|tomar|gaseosa|gaseosas|jugo|jugos|cerveza|cervezas|agua|aguas|vino|vinos|trago|tragos)/i.test(question);

const isDessertQuestion = (question) =>
  /(postre|dulce|helado|torta|flan|budin|budín)/i.test(question);

const isLightQuestion = (question) =>
  /(livian|liger|suave|saludable|ensalada|poco pesado)/i.test(question);

const isHeartyQuestion = (question) =>
  /(hambre|abundante|llenador|fuerte|completo|grande)/i.test(question);

const parseBudgetLimit = (question) => {
  const normalizedQuestion = normalizeSearchText(question).replace(/\$/g, " ");
  const match = normalizedQuestion.match(
    /(?:hasta|maximo|max|menos de|menor a|por debajo de|barato de)\s+([0-9][0-9.,]*)(\s*mil)?/
  );

  if (!match) {
    return 0;
  }

  const parsed = Number(String(match[1] || "").replace(/\./g, "").replace(",", "."));

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }

  return match[2] && parsed < 1000 ? parsed * 1000 : parsed;
};

const getQuestionIntent = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);
  const budgetLimit = parseBudgetLimit(question);

  return {
    asksForList:
      /(que .*tenes|que .*tiene(?:n|s)?|que .*hay|opciones|cuales|lista|mostrame|muestrame|mostrar)/i.test(
        normalizedQuestion
      ) ||
      /\b(que .*tienen|que .*ofrecen|que .*venden|hay .*para tomar|bebidas disponibles)\b/i.test(
        normalizedQuestion
      ) ||
      isDrinkFamilyQuestion(normalizedQuestion),
    asksForComparison:
      /(compar|diferencia|cual conviene|cual es mejor|entre .* y )/i.test(
        normalizedQuestion
      ),
    asksForBudget:
      budgetLimit > 0 ||
      /(barat|econom|accesible|menos caro|precio bajo)/i.test(normalizedQuestion),
    asksForLight: isLightQuestion(normalizedQuestion),
    asksForHearty: isHeartyQuestion(normalizedQuestion),
    asksForDrink: isDrinkQuestion(normalizedQuestion),
    asksForFood: isFoodFamilyQuestion(normalizedQuestion),
    asksForFoodOnly: isFoodOnlyQuestion(normalizedQuestion),
    asksForDessert: isDessertQuestion(normalizedQuestion),
    budgetLimit,
  };
};

const rankProductsForQuestion = (question, products = [], { limit = 6 } = {}) => {
  const intent = getQuestionIntent(question);
  const ranked = (Array.isArray(products) ? products : [])
    .map((product, index) => {
      const price = getProductPrice(product);
      let score = scoreProductForQuestion(question, product);

      if (intent.asksForBudget && intent.budgetLimit > 0) {
        score += price > 0 && price <= intent.budgetLimit ? 8 : -8;
      }

      if (intent.asksForBudget && !intent.budgetLimit && price > 0) {
        score += Math.max(0, 8 - Math.floor(price / 5000));
      }

      return {
        product,
        index,
        price,
        score,
      };
    })
    .filter((entry) => entry.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      if (intent.asksForBudget && left.price !== right.price) {
        return left.price - right.price;
      }

      return right.price - left.price || left.index - right.index;
    });

  return ranked.slice(0, limit).map((entry) => entry.product);
};

const pickRecommendationProducts = (question, products = []) => {
  const source = Array.isArray(products) ? products : [];

  if (source.length === 0) {
    return [];
  }

  const intent = getQuestionIntent(question);
  const normalizedProducts = source.map((product) => ({
    ...product,
    haystack: getProductSearchHaystack(product),
    queryScore: scoreProductForQuestion(question, product),
  }));
  const directMatches = normalizedProducts
    .filter((product) => product.queryScore > 0)
    .sort((left, right) => {
      if (right.queryScore !== left.queryScore) {
        return right.queryScore - left.queryScore;
      }

      return (
        toMoney(right?.price ?? right?.suggestedPrice) -
        toMoney(left?.price ?? left?.suggestedPrice)
      );
    });

  if (directMatches.length > 0) {
    return directMatches.slice(0, 3).map(({ haystack, queryScore, ...product }) => product);
  }

  let filtered = normalizedProducts;

  if (intent.asksForBudget && intent.budgetLimit > 0) {
    filtered = normalizedProducts.filter((product) => {
      const price = getProductPrice(product);
      return price > 0 && price <= intent.budgetLimit;
    });
  } else if (isDrinkQuestion(question)) {
    filtered = normalizedProducts.filter((product) =>
      /(bebida|jugo|agua|gaseosa|cerveza|vino|trago)/.test(product.haystack)
    );
  } else if (isDessertQuestion(question)) {
    filtered = normalizedProducts.filter((product) =>
      /(postre|helado|torta|flan|choco|dulce)/.test(product.haystack)
    );
  } else if (isLightQuestion(question)) {
    filtered = normalizedProducts.filter((product) =>
      /(ensalada|pollo|wrap|veggie|vegetar|livian)/.test(product.haystack)
    );
  } else if (isHeartyQuestion(question)) {
    filtered = normalizedProducts.filter((product) =>
      /(milanesa|burger|hamburguesa|pizza|lomito|pasta|parrilla|sanduich)/.test(
        product.haystack
      )
    );
  }

  const finalSelection = (filtered.length > 0 ? filtered : normalizedProducts)
    .sort((left, right) => {
      const leftPrice = toMoney(left?.price ?? left?.suggestedPrice);
      const rightPrice = toMoney(right?.price ?? right?.suggestedPrice);
      if (intent.asksForBudget) {
        return leftPrice - rightPrice;
      }
      return rightPrice - leftPrice;
    })
    .slice(0, 3)
    .map(({ haystack, queryScore, ...product }) => product);

  return finalSelection;
};

const formatProductPrice = (product) => {
  const price = toMoney(product?.price ?? product?.suggestedPrice);
  return price > 0 ? currencyFormatter.format(price) : "";
};

const formatProductIngredientSummary = (product) => {
  const ingredients = getProductIngredients(product).slice(0, 6);

  if (ingredients.length === 0) {
    return "No tengo el detalle de ingredientes cargado en este menu.";
  }

  return `Lleva ${ingredients.join(", ")}.`;
};

const createIngredientBaseSelection = (product = {}) =>
  (Array.isArray(product?.ingredients) ? product.ingredients : []).reduce(
    (acc, ingredient) => {
      acc[ingredient.id] = Number(ingredient?.baseQuantity ?? 0);
      return acc;
    },
    {}
  );

const tokenizeIngredientComparableText = (value = "") =>
  normalizeSearchText(value)
    .split(/[^a-z0-9]+/g)
    .filter((token) => token.length >= 3);

const buildIngredientSegmentMatches = (segment = "", ingredients = []) => {
  const safeSegment = normalizeSearchText(segment);

  if (!safeSegment) {
    return [];
  }

  const tokenFrequency = new Map();

  (Array.isArray(ingredients) ? ingredients : []).forEach((ingredient) => {
    tokenizeIngredientComparableText(ingredient?.name || ingredient?.nombre).forEach(
      (token) => {
        if (token.length < 4) {
          return;
        }

        tokenFrequency.set(token, Number(tokenFrequency.get(token) || 0) + 1);
      }
    );
  });

  return (Array.isArray(ingredients) ? ingredients : []).filter((ingredient) => {
    const ingredientName = toSafeText(ingredient?.name || ingredient?.nombre, 120);
    const normalizedName = normalizeSearchText(ingredientName);
    const ingredientTokens = tokenizeIngredientComparableText(ingredientName);

    if (!normalizedName || !ingredientTokens.length) {
      return false;
    }

    if (safeSegment.includes(normalizedName)) {
      return true;
    }

    if (
      ingredientTokens.length >= 2 &&
      ingredientTokens.every((token) => safeSegment.includes(token))
    ) {
      return true;
    }

    return ingredientTokens.some(
      (token) =>
        token.length >= 4 &&
        safeSegment.includes(token) &&
        tokenFrequency.get(token) === 1
    );
  });
};

export const buildPublicMenuAssistantIngredientSelectionPlan = (
  question = "",
  product = {}
) => {
  const ingredients = Array.isArray(product?.ingredients) ? product.ingredients : [];
  const normalizedQuestion = normalizeSearchText(question);
  const hasCustomizationIntent =
    ingredients.length > 0 &&
    /\b(sin|sacale|sacar|quitar|quitale|quitame|extra|doble|sumale|agregale|agrega|agregar|mas|menos)\b/.test(
      normalizedQuestion
    );

  if (!hasCustomizationIntent) {
    return {
      hasCustomizationIntent: false,
      hasChanges: false,
      needsManualReview: false,
      selection: null,
      summaryParts: [],
      summaryText: "",
    };
  }

  const nextSelection = createIngredientBaseSelection(product);
  const segments = normalizedQuestion
    .split(
      /(?=\b(?:sin|sacale|sacar|quitar|quitale|quitame|extra|doble|sumale|agregale|agrega|agregar|mas|menos)\b)/g
    )
    .map((segment) => segment.trim())
    .filter(Boolean);
  const summaryParts = [];
  let matchedAnyIngredient = false;
  let blockedChange = false;

  segments.forEach((segment) => {
    const safeSegment = normalizeSearchText(segment);
    const isRemovalSegment = /^(sin|sacale|sacar|quitar|quitale|quitame|menos)\b/.test(
      safeSegment
    );
    const isExtraSegment = /^(extra|doble|sumale|agregale|agrega|agregar|mas)\b/.test(
      safeSegment
    );

    if (!isRemovalSegment && !isExtraSegment) {
      return;
    }

    const matchedIngredients = buildIngredientSegmentMatches(segment, ingredients);

    if (!matchedIngredients.length) {
      return;
    }

    matchedAnyIngredient = true;

    matchedIngredients.forEach((ingredient) => {
      const ingredientId = toSafeText(ingredient?.id, 120);
      const ingredientName = toSafeText(ingredient?.name || ingredient?.nombre, 120);
      const baseQuantity = Number(ingredient?.baseQuantity ?? 0);
      const currentQuantity = Number(nextSelection[ingredientId] ?? baseQuantity);

      if (!ingredientId || !ingredientName) {
        return;
      }

      if (isRemovalSegment) {
        if (ingredient?.canRemove !== true || currentQuantity <= 0) {
          blockedChange = true;
          return;
        }

        if (currentQuantity !== 0) {
          nextSelection[ingredientId] = 0;
          summaryParts.push(`sin ${ingredientName}`);
        }
        return;
      }

      if (ingredient?.canAddMore !== true) {
        blockedChange = true;
        return;
      }

      const extraLimit = Math.max(1, Number(ingredient?.maxExtraQuantity || 1));
      const maxQuantity = baseQuantity + extraLimit;
      const requestedIncrement =
        /^doble\b/.test(safeSegment) && baseQuantity > 0
          ? Math.max(1, baseQuantity)
          : 1;
      const nextQuantity = Math.min(maxQuantity, currentQuantity + requestedIncrement);

      if (nextQuantity <= currentQuantity) {
        blockedChange = true;
        return;
      }

      nextSelection[ingredientId] = nextQuantity;
      summaryParts.push(
        /^doble\b/.test(safeSegment)
          ? `doble ${ingredientName}`
          : `extra ${ingredientName}`
      );
    });
  });

  return {
    hasCustomizationIntent: true,
    hasChanges: summaryParts.length > 0,
    needsManualReview:
      summaryParts.length === 0 ||
      (!matchedAnyIngredient && hasCustomizationIntent) ||
      blockedChange,
    selection: summaryParts.length > 0 ? nextSelection : null,
    summaryParts,
    summaryText: summaryParts.join(", "),
  };
};

const formatProductLine = (product = {}, { includeIngredients = false } = {}) => {
  const price = formatProductPrice(product);
  const description = toSafeText(product?.description || "", 100);
  const ingredients = includeIngredients
    ? getProductIngredients(product).slice(0, 4).join(", ")
    : "";

  return [
    product?.name || "Producto",
    price ? price : "",
    description ? description : "",
    ingredients ? `lleva ${ingredients}` : "",
  ]
    .filter(Boolean)
    .join(" - ");
};

const isProductDescriptionRedundant = (product = {}) => {
  const name = normalizeSearchText(product?.name || "");
  const description = normalizeSearchText(product?.description || "");

  if (!name || !description) {
    return false;
  }

  if (description.includes(name) || name.includes(description)) {
    return true;
  }

  const nameTokens = new Set(tokenize(name));
  const descriptionTokens = tokenize(description);

  if (!nameTokens.size || !descriptionTokens.length) {
    return false;
  }

  const repeatedTokens = descriptionTokens.filter((token) => nameTokens.has(token));
  return repeatedTokens.length / Math.max(1, descriptionTokens.length) >= 0.6;
};

const formatProductDecisionSummary = (product = {}) => {
  const description = toSafeText(product?.description || "", 150);

  if (description && !isProductDescriptionRedundant(product)) {
    return description;
  }

  const categoryHint = toSafeText(
    product?.subcategoryName || product?.subcategoria || product?.categoryName || "",
    80
  );

  if (categoryHint) {
    return `La tengo dentro de ${categoryHint}; te conviene abrirla para ver si va con acompanamiento o ajuste.`;
  }

  return "Te conviene abrirla para ver detalle y elegirla como plato principal.";
};

const summarizeRelatedProduct = (product = {}, { includeDescription = true } = {}) => ({
  productId: toSafeText(product?.id || product?.productId || "", 120),
  name: toSafeText(product?.name || "Producto", 120),
  description: includeDescription ? toSafeText(product?.description || "", 160) : "",
  imageUrl: /^data:/i.test(String(product?.imageUrl || product?.fotoUrl || ""))
    ? ""
    : toSafeText(product?.imageUrl || product?.fotoUrl || "", 1200),
  price: toMoney(product?.price ?? product?.suggestedPrice),
});

const buildAssistantRelatedProductsFromIds = (
  productIds = [],
  products = [],
  { includeDescription = true } = {}
) => {
  const normalizedIds = normalizeAssistantStringList(productIds, 16, 120);

  if (!normalizedIds.length) {
    return [];
  }

  const productsById = new Map(
    (Array.isArray(products) ? products : []).map((product) => [
      toSafeText(product?.id || product?.productId || "", 120),
      product,
    ])
  );

  return normalizedIds
    .map((productId) => productsById.get(productId))
    .filter(Boolean)
    .map((product) => summarizeRelatedProduct(product, { includeDescription }));
};

const buildAssistantRelatedProducts = ({
  question = "",
  answer = "",
  suggestions = [],
  products = [],
  linkedRules = [],
} = {}) => {
  const normalizedProducts = filterPublicMenuAssistantProductsForQuestion(
    question,
    products
  );
  const intent = getQuestionIntent(question);
  const includeDescription = !intent.asksForList;
  const strictListProducts = intent.asksForList
    ? getStrictListProductsForQuestion(question, normalizedProducts, { limit: 10 })
    : [];

  if (intent.asksForList && strictListProducts.length > 0) {
    const pairingProducts = summarizePairingProducts(
      buildMenuPairingContexts({
        question,
        products: normalizedProducts,
        linkedRules,
        sourceProducts: strictListProducts.slice(0, 4),
        limit: 6,
      })
    );
    const productMap = new Map();

    [...strictListProducts, ...pairingProducts].forEach((product) => {
      const key =
        toSafeText(product?.id || product?.productId || "", 120) ||
        normalizeSearchText(product?.name || "");

      if (!key || productMap.has(key)) {
        return;
      }

      productMap.set(key, summarizeRelatedProduct(product, { includeDescription }));
    });

    return Array.from(productMap.values()).slice(0, 10);
  }

  const suggestedProducts = pickRecommendationProducts(
    [question, answer].filter(Boolean).join(" "),
    normalizedProducts
  );
  const matchedProducts = findMatchingProducts(
    [question, answer, ...(Array.isArray(suggestions) ? suggestions : [])]
      .filter(Boolean)
      .join(" "),
    normalizedProducts
  );
  const pairingProducts = summarizePairingProducts(
    buildMenuPairingContexts({
      question,
      products: normalizedProducts,
      linkedRules,
      sourceProducts: [...matchedProducts, ...suggestedProducts],
    })
  );
  const productMap = new Map();

  [...matchedProducts, ...suggestedProducts, ...pairingProducts].forEach((product) => {
    const key =
      toSafeText(product?.id || product?.productId || "", 120) ||
      normalizeSearchText(product?.name || "");

    if (!key || productMap.has(key)) {
      return;
    }

    productMap.set(key, summarizeRelatedProduct(product, { includeDescription }));
  });

  return Array.from(productMap.values()).slice(0, intent.asksForList ? 10 : 6);
};

const ORDER_QUANTITY_WORDS = {
  un: 1,
  una: 1,
  uno: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
};

const extractOrderQuantity = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);
  const numericMatch = normalizedQuestion.match(/\b([1-9]|1[0-9]|20)\b/);

  if (numericMatch) {
    return Math.max(1, Math.min(20, Number(numericMatch[1]) || 1));
  }

  const wordMatch = normalizedQuestion.match(
    /\b(un|una|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\b/
  );

  if (wordMatch) {
    return ORDER_QUANTITY_WORDS[wordMatch[1]] || 1;
  }

  return 1;
};

const isCartReviewRequest = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  return /\b(mi pedido|pedido|carrito|orden)\b/.test(normalizedQuestion) &&
    /\b(ver|mostrar|abrir|revisar|cambiar|modificar|editar|corregir)\b/.test(
      normalizedQuestion
    );
};

const isRemoveOrderRequest = (question = "") =>
  /\b(saca|sacame|sacar|quita|quitame|quitar|borra|borrame|borrar|elimina|eliminame|eliminar|remove)\b/.test(
    normalizeSearchText(question)
  );

const isReplaceOrderRequest = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  return /\b(cambia|cambiame|cambiar|reemplaza|reemplazame|reemplazar|en vez de)\b/.test(
    normalizedQuestion
  ) && /\b(por|x|en vez de|mejor)\b/.test(normalizedQuestion);
};

const hasExplicitAddOrderVerb = (normalizedQuestion = "") =>
  /\b(agrega|agregale|agregame|agregas|agregues|agregar|sumale|sumame|sumas|sumes|suma|sumar|ponele|poneme|pone|pones|pongas|poner|cargale|cargame|cargas|cargues|carga|cargar|pedime|pedi|pedir|pido|anadi|anadile|anadime|anadis|anadas|dame|me das|mandame|mandas|mandes|llevo)\b/.test(
    normalizedQuestion
  ) || /\bquiero que me (agregues|sumes|pongas|cargues|mandes|des)\b/.test(normalizedQuestion);

const isOrderRequest = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  if (hasExplicitAddOrderVerb(normalizedQuestion)) {
    return true;
  }

  if (
    /\b(agrega|agregale|agregame|agregar|sumale|sumame|suma|sumar|ponele|poneme|pone|poner|cargale|cargame|carga|pedime|pedir|anadi|anadile|anadime|añadi|añadile|añadime|dame|me das|mandame|llevo)\b/.test(
      normalizedQuestion
    )
  ) {
    return true;
  }

  return /\bquiero\b/.test(normalizedQuestion) &&
    !/\b(algo|recom|suger|opcion|opciones|que|cual|cuales|ver|mirar)\b/.test(
      normalizedQuestion
    );
};

const pickOrderProductCandidate = (question = "", products = []) => {
  const matchedProducts = rankProductsForQuestion(question, products, { limit: 8 });

  if (matchedProducts.length === 0) {
    return null;
  }

  const primaryProduct = matchedProducts.find(
    (product) => !isLikelyAccompanimentProduct(product)
  );

  return primaryProduct || matchedProducts[0];
};

const productNameLooksMentioned = (question = "", product = {}) => {
  const normalizedQuestion = normalizeSearchText(question);
  const productName = normalizeSearchText(product?.name || "");

  if (!normalizedQuestion || !productName) {
    return false;
  }

  if (normalizedQuestion.includes(productName)) {
    return true;
  }

  const questionTokens = new Set(expandQueryTokens(normalizedQuestion));
  const variantMatch = productName.match(/^(.+?)\s+con\s+(.+)$/);

  if (variantMatch) {
    const optionTokens = expandQueryTokens(variantMatch[2]).filter(
      (token) => token.length >= 4 && !QUERY_STOP_WORDS.has(token)
    );
    const hasOptionMention = optionTokens.some((token) => questionTokens.has(token));

    if (!hasOptionMention) {
      return false;
    }
  }

  const productTokens = expandQueryTokens(productName).filter(
    (token) =>
      token.length >= 4 &&
      !QUERY_STOP_WORDS.has(token) &&
      !["con", "para", "del"].includes(token)
  );

  if (!productTokens.length) {
    return false;
  }

  const matchedTokens = productTokens.filter((token) => questionTokens.has(token));
  return matchedTokens.length >= Math.min(productTokens.length, 2);
};

const cleanSplitOrderBaseText = (value = "") =>
  normalizeSearchText(value)
    .replace(
      /\b(quiero|agrega|agregame|agregas|agregues|agregar|sumame|sumas|sumes|suma|sumar|poneme|pone|pones|pongas|poner|cargame|cargas|cargues|carga|cargar|pedime|pedi|pedir|pido|dame|mandame|mandas|mandes|llevo|me|das|por|favor)\b/g,
      " "
    )
    .replace(
      /\b(un|una|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|1|2|3|4|5|6|7|8|9|10)\b/g,
      " "
    )
    .replace(/\s+/g, " ")
    .trim();

const buildSplitCompanionOrderFragments = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);
  const match = normalizedQuestion.match(
    /^(.*?)\b(?:un|una|uno|1)\s+con\s+(.+?)\s+y\s+(?:(?:la|el)\s+)?otr[oa]\s+con\s+(.+)$/
  );

  if (!match) {
    return [];
  }

  const baseText = cleanSplitOrderBaseText(match[1]);
  const firstOptionText = toSafeText(match[2], 120);
  const secondOptionText = toSafeText(match[3], 120);

  if (!baseText || !firstOptionText || !secondOptionText) {
    return [];
  }

  return [
    `${baseText} con ${firstOptionText}`,
    `${baseText} con ${secondOptionText}`,
  ];
};

const buildSplitCompanionOrderItems = ({
  question = "",
  products = [],
  linkedRules = [],
} = {}) => {
  const fragments = buildSplitCompanionOrderFragments(question);

  if (fragments.length < 2) {
    return [];
  }

  const items = fragments
    .map((fragment) => {
      const product = pickOrderProductCandidate(fragment, products);

      if (!product) {
        return null;
      }

      return {
        product,
        productId: toSafeText(product?.id || product?.productId, 120),
        quantity: 1,
        question: fragment,
        ingredientSelectionPlan: buildPublicMenuAssistantIngredientSelectionPlan(
          fragment,
          product
        ),
        relatedProducts: buildAssistantRelatedProducts({
          question: fragment,
          answer: "",
          products,
          linkedRules,
        }),
      };
    })
    .filter(Boolean);

  return items.length === fragments.length ? items : [];
};

const getCartItemSearchText = (item = {}) =>
  normalizeSearchText(
    [
      item?.productName,
      item?.name,
      item?.linkedProductName,
      ...(Array.isArray(item?.selectionDetails) ? item.selectionDetails : []),
    ]
      .filter(Boolean)
      .join(" ")
  );

const scoreCartItemForQuestion = (question = "", item = {}) => {
  const normalizedQuestion = normalizeSearchText(question);
  const terms = expandQueryTokens(question);
  const productName = normalizeSearchText(item?.productName || item?.name || "");
  const searchText = getCartItemSearchText(item);
  let score = 0;

  if (!searchText) {
    return 0;
  }

  if (productName && normalizedQuestion.includes(productName)) {
    score += 30;
  }

  terms.forEach((term) => {
    if (productName.includes(term)) {
      score += 12;
    } else if (searchText.includes(term)) {
      score += 4;
    }
  });

  return score;
};

const pickCartItemCandidate = (question = "", cartItems = []) => {
  const rankedItems = (Array.isArray(cartItems) ? cartItems : [])
    .map((item, index) => ({
      item,
      index,
      score: scoreCartItemForQuestion(question, item),
    }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index);

  return rankedItems[0]?.item || null;
};

const extractReplaceOrderParts = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);
  const match =
    normalizedQuestion.match(
      /\b(?:cambia|cambiame|cambiar|reemplaza|reemplazame|reemplazar)\b\s+(.+?)\s+\b(?:por|x)\b\s+(.+)$/
    ) ||
    normalizedQuestion.match(/\ben vez de\b\s+(.+?)\s+\b(?:pone|poneme|quiero|dame|agrega|agregame)\b\s+(.+)$/);

  if (!match) {
    return {
      sourceText: normalizedQuestion,
      targetText: normalizedQuestion,
    };
  }

  return {
    sourceText: toSafeText(match[1], 180),
    targetText: toSafeText(match[2], 180),
  };
};

const isBroadFamilyOrderQuestion = (question = "", product = {}) => {
  const normalizedQuestion = normalizeSearchText(question);
  const productName = normalizeSearchText(product?.name || "");

  if (productName && productNameLooksMentioned(normalizedQuestion, product)) {
    return false;
  }

  return (
    isDrinkFamilyQuestion(normalizedQuestion) ||
    /\b(pasta|pastas|milanesa|milanesas|hamburguesa|hamburguesas|pizza|pizzas|postre|postres)\b/.test(
      normalizedQuestion
    )
  );
};

const buildBroadFamilyBrowseAnswer = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  if (/\b(mila|milanesa|milanesas|suprema|supremas)\b/.test(normalizedQuestion)) {
    return "Te muestro las milanesas disponibles. Elige la que quieras y despues puedes confirmar o quitar ingredientes antes de agregarla.";
  }

  if (/\b(pasta|pastas|fideo|fideos|raviol|ravioles|sorrentino|sorrentinos|noqui|noquis)\b/.test(normalizedQuestion)) {
    return "Te muestro las pastas disponibles. Elige una y despues vemos la salsa o los ajustes que quieras hacer.";
  }

  if (/\b(hamburguesa|hamburguesas|burger|burgers)\b/.test(normalizedQuestion)) {
    return "Te muestro las hamburguesas disponibles. Elige una y despues puedes revisar o cambiar sus ingredientes.";
  }

  if (/\b(pizza|pizzas|muzza|muzzarella|fugazza)\b/.test(normalizedQuestion)) {
    return "Te muestro las pizzas disponibles. Elige una para ver sus ingredientes y confirmarla en tu pedido.";
  }

  if (/\b(bebida|bebidas|tomar|beber|gaseosa|gaseosas|jugo|jugos|cerveza|cervezas|vino|vinos)\b/.test(normalizedQuestion)) {
    return "Te muestro las bebidas disponibles. Elige la que quieras agregar al pedido.";
  }

  return "Te muestro las opciones disponibles. Elige una tarjeta para revisar sus ingredientes antes de agregarla.";
};

// A family word such as "milanesa" is not enough to choose a concrete variant.
// Keep the request attached to each card so the product dialog can prefill it later.
const GENERIC_ORDER_FAMILIES = [
  {
    key: "milanesa",
    label: "milanesa",
    matcher: /\b(?:mila|milas|milanesa|milanesas|suprema|supremas)\b/,
  },
  {
    key: "hamburguesa",
    label: "hamburguesa",
    matcher: /\b(?:hamburguesa|hamburguesas|burger|burgers)\b/,
  },
];

const getGenericFamilyProducts = (family = {}, products = []) =>
  (Array.isArray(products) ? products : []).filter((product) =>
    family?.matcher?.test(getProductSearchHaystack(product))
  );

const GENERIC_FAMILY_PRODUCT_TOKENS = new Set([
  "mila",
  "milas",
  "milanesa",
  "milanesas",
  "suprema",
  "supremas",
  "hamburguesa",
  "hamburguesas",
  "burger",
  "burgers",
]);

const getLiteralProductTokens = (value = "") =>
  normalizeSearchText(value)
    .split(/[^a-z0-9]+/g)
    .filter(
      (token) =>
        token.length >= 3 &&
        !QUERY_STOP_WORDS.has(token) &&
        !GENERIC_FAMILY_PRODUCT_TOKENS.has(token)
    );

const scoreConcreteFamilyProductMention = (question = "", product = {}) => {
  const normalizedQuestion = normalizeSearchText(question);
  const productName = normalizeSearchText(product?.name || product?.nombre || "");

  if (!normalizedQuestion || !productName) return 0;
  if (normalizedQuestion.includes(productName)) return 100;

  const questionTokens = getLiteralProductTokens(normalizedQuestion);
  return getLiteralProductTokens(productName).reduce((score, productToken) => {
    const exactMatch = questionTokens.some(
      (questionToken) => questionToken === productToken
    );
    if (exactMatch) return score + 2;

    const prefixMatch = questionTokens.some(
      (questionToken) =>
        questionToken.length >= 4 &&
        productToken.length >= 4 &&
        (productToken.startsWith(questionToken) ||
          questionToken.startsWith(productToken))
    );
    return score + (prefixMatch ? 1 : 0);
  }, 0);
};

const findExplicitConcreteFamilyProduct = (
  question = "",
  familyProducts = []
) => {
  const normalizedQuestion = normalizeSearchText(question);
  const ranked = (Array.isArray(familyProducts) ? familyProducts : [])
    .map((product, index) => ({
      product,
      index,
      score: scoreConcreteFamilyProductMention(question, product),
      literalTokenCount: getLiteralProductTokens(
        product?.name || product?.nombre || ""
      ).length,
    }))
    .sort(
      (left, right) =>
        right.score - left.score || left.index - right.index
    );
  const best = ranked[0];
  const second = ranked[1];

  if (/\b(simple|sola|solo|clasica|clasico|comun|tradicional)\b/.test(normalizedQuestion)) {
    const simplestCandidates = ranked
      .filter((entry) => entry.score === (best?.score || 0))
      .sort(
        (left, right) =>
          left.literalTokenCount - right.literalTokenCount ||
          normalizeSearchText(left.product?.name || "").length -
            normalizeSearchText(right.product?.name || "").length ||
          left.index - right.index
      );
    const simplest = simplestCandidates[0];
    const nextSimplest = simplestCandidates[1];

    if (
      simplest &&
      (!nextSimplest || simplest.literalTokenCount < nextSimplest.literalTokenCount)
    ) {
      return simplest.product;
    }
  }

  if (!best || best.score <= 0) return null;
  if (second && second.score === best.score) return null;
  return best.product;
};

const hasExplicitConcreteFamilyProduct = (question = "", familyProducts = []) =>
  Boolean(findExplicitConcreteFamilyProduct(question, familyProducts));

const extractFamilyRequestPhrase = (question = "", family = {}) => {
  const normalizedQuestion = normalizeSearchText(question);
  const match = normalizedQuestion.match(family?.matcher);

  if (!match || typeof match.index !== "number") {
    return family?.label || "producto";
  }

  const suffix = normalizedQuestion
    .slice(match.index + match[0].length)
    .split(/(?:,|;|\by\s+(?=(?:dos|2|un|una|otro|otra)\b))/)[0]
    .trim();

  return toSafeText(`${family.label || match[0]} ${suffix}`.trim(), 180);
};

const buildGenericFamilySelectionRequests = (question = "", products = []) => {
  const normalizedQuestion = normalizeSearchText(question);
  const requests = [];
  const handledFamilyKeys = new Set();
  const burgerSplitMatch = normalizedQuestion.match(
    /\b(?:dos|2)\s+(?:hamburguesas?|burgers?)\s*,?\s*(?:una|un|1)\s+(sin\s+[^,;]+?)\s*(?:,?\s+y\s+(?:la|el)\s*)?otr[oa]\s+(sin\s+[^,;.]+)(?=$|[,;.])/
  );

  if (burgerSplitMatch) {
    [burgerSplitMatch[1], burgerSplitMatch[2]].forEach((adjustment) => {
      const cleanAdjustment = toSafeText(adjustment, 120);
      if (!cleanAdjustment) {
        return;
      }

      requests.push({
        familyKey: "hamburguesa",
        label: `Hamburguesa ${cleanAdjustment}`,
        question: `hamburguesa ${cleanAdjustment}`,
      });
    });
    handledFamilyKeys.add("hamburguesa");
  }

  GENERIC_ORDER_FAMILIES.forEach((family) => {
    if (handledFamilyKeys.has(family.key) || !family.matcher.test(normalizedQuestion)) {
      return;
    }

    const familyProducts = getGenericFamilyProducts(family, products);
    if (!familyProducts.length || hasExplicitConcreteFamilyProduct(question, familyProducts)) {
      return;
    }

    const requestQuestion = extractFamilyRequestPhrase(question, family);
    requests.push({
      familyKey: family.key,
      label: requestQuestion.charAt(0).toUpperCase() + requestQuestion.slice(1),
      question: requestQuestion,
    });
  });

  return requests;
};

const buildGenericFamilySelectionDraft = ({ question = "", products = [] } = {}) => {
  const requests = buildGenericFamilySelectionRequests(question, products);

  if (!requests.length) {
    return null;
  }

  const request = requests[0];
  const family = GENERIC_ORDER_FAMILIES.find(
    (item) => item.key === request.familyKey
  );
  const optionNames = getGenericFamilyProducts(family, products)
    .slice(0, 4)
    .map((product) => getProductName(product))
    .filter(Boolean);
  const answer = optionNames.length
    ? `Para tomar bien el pedido, cual queres: ${joinNaturalOptions(
        optionNames,
        "o"
      )}?`
    : `No encontre opciones de ${request.label} en la carta actual.`;

  return {
    shouldHandle: true,
    status: optionNames.length ? "clarify" : "not_found",
    action: optionNames.length ? "clarify" : "",
    quantity: 1,
    answer,
    suggestions: optionNames,
    relatedProducts: [],
  };
};

const buildStandaloneCompanionOrderItems = ({
  question = "",
  product = null,
  products = [],
  linkedRules = [],
} = {}) => {
  if (!product || isLikelyAccompanimentProduct(product)) {
    return [];
  }

  const productId = toSafeText(product?.id || product?.productId, 120);
  const normalizedQuestion = normalizeSearchText(question);
  const linkedOptionIds = new Set();

  (Array.isArray(linkedRules) ? linkedRules : []).forEach((rule) => {
    if (!matchesLinkedRule(rule, product)) {
      return;
    }

    resolveExplicitLinkedProducts(rule, products, product).forEach((option) => {
      linkedOptionIds.add(toSafeText(option?.id || option?.productId, 120));
    });
  });

  const companions = (Array.isArray(products) ? products : [])
    .filter((candidate) => {
      const candidateId = toSafeText(candidate?.id || candidate?.productId, 120);

      return (
        candidateId &&
        candidateId !== productId &&
        !linkedOptionIds.has(candidateId) &&
        isLikelyAccompanimentProduct(candidate) &&
        normalizedQuestion.includes(
          normalizeSearchText(candidate?.name || candidate?.nombre || "")
        )
      );
    })
    .slice(0, 3);

  if (!companions.length) {
    return [];
  }

  return [product, ...companions].map((itemProduct, index) => ({
    product: itemProduct,
    productId: toSafeText(itemProduct?.id || itemProduct?.productId, 120),
    quantity: index === 0 ? extractOrderQuantity(question) : 1,
    question,
    ingredientSelectionPlan: buildPublicMenuAssistantIngredientSelectionPlan(
      question,
      itemProduct
    ),
    relatedProducts: [summarizeRelatedProduct(itemProduct)],
  }));
};

export const buildPublicMenuAssistantOrderDraft = ({
  question = "",
  products = [],
  linkedRules = [],
  cartItems = [],
} = {}) => {
  const safeQuestion = toSafeText(question, 320);
  const normalizedQuestion = normalizeSearchText(safeQuestion);
  const { products: relationProducts, standaloneProducts: normalizedProducts } = getAssistantMenuCatalog(products, linkedRules);
  const normalizedCartItems = Array.isArray(cartItems) ? cartItems : [];

  if (!safeQuestion || (!normalizedProducts.length && !normalizedCartItems.length)) {
    return {
      shouldHandle: false,
      status: "none",
    };
  }

  if (isReplaceOrderRequest(safeQuestion)) {
    const replaceParts = extractReplaceOrderParts(safeQuestion);
    const cartItem = pickCartItemCandidate(
      replaceParts.sourceText || safeQuestion,
      normalizedCartItems
    );
    const product = pickOrderProductCandidate(
      replaceParts.targetText || safeQuestion,
      normalizedProducts
    );

    if (!cartItem || !product) {
      return {
        shouldHandle: true,
        status: "not_found",
        action: "open_cart",
        answer:
          "No pude identificar con claridad que producto queres cambiar. Te abro Mi pedido para revisarlo.",
        relatedProducts: buildAssistantRelatedProducts({
          question: safeQuestion,
          answer: "",
          products: normalizedProducts,
          linkedRules,
        }),
      };
    }

    return {
      shouldHandle: true,
      status: "ready",
      action: "replace",
      cartItemId: toSafeText(cartItem?.id, 160),
      cartProductName: toSafeText(cartItem?.productName || cartItem?.name, 120),
      product,
      productId: toSafeText(product?.id || product?.productId, 120),
      quantity: extractOrderQuantity(replaceParts.targetText || safeQuestion),
      ingredientSelectionPlan: buildPublicMenuAssistantIngredientSelectionPlan(
        replaceParts.targetText || safeQuestion,
        product
      ),
      relatedProducts: [summarizeRelatedProduct(product)],
    };
  }

  if (isRemoveOrderRequest(safeQuestion)) {
    const cartItem = pickCartItemCandidate(safeQuestion, normalizedCartItems);

    if (!cartItem) {
      return {
        shouldHandle: true,
        status: "not_found",
        action: "open_cart",
        answer:
          "No pude identificar cual producto queres quitar. Te abro Mi pedido para revisarlo.",
        relatedProducts: [],
      };
    }

    return {
      shouldHandle: true,
      status: "ready",
      action: "remove",
      cartItemId: toSafeText(cartItem?.id, 160),
      cartProductName: toSafeText(cartItem?.productName || cartItem?.name, 120),
      quantity: extractOrderQuantity(safeQuestion),
      relatedProducts: [],
    };
  }

  if (isCartReviewRequest(safeQuestion) && !isOrderRequest(safeQuestion)) {
    return {
      shouldHandle: true,
      status: "ready",
      action: "open_cart",
      answer: normalizedCartItems.length
        ? "Te abro Mi pedido para que puedas revisar o cambiar lo que cargaste."
        : "Todavia no tenes productos cargados. Te muestro la carta para elegir.",
      relatedProducts: [],
    };
  }

  if (!isOrderRequest(safeQuestion) || !normalizedProducts.length) {
    return {
      shouldHandle: false,
      status: "none",
    };
  }

  if (
    !hasExplicitAddOrderVerb(normalizedQuestion) &&
    /\b(algo|recom|suger|opcion|opciones|rico|liviano|barato|que|cual|cuales|puedo|conviene)\b/.test(
      normalizedQuestion
    )
  ) {
    return {
      shouldHandle: false,
      status: "none",
    };
  }

  const matchingGenericFamily = GENERIC_ORDER_FAMILIES.find((family) =>
    family.matcher.test(normalizedQuestion)
  );
  const concreteFamilyProduct = matchingGenericFamily
    ? findExplicitConcreteFamilyProduct(
        safeQuestion,
        getGenericFamilyProducts(matchingGenericFamily, normalizedProducts)
      )
    : null;
  const concreteProductCandidate =
    concreteFamilyProduct ||
    pickOrderProductCandidate(safeQuestion, normalizedProducts);
  const hasConcreteProductMatch = matchingGenericFamily
    ? Boolean(concreteFamilyProduct)
    : Boolean(
        concreteProductCandidate &&
          productNameLooksMentioned(safeQuestion, concreteProductCandidate)
      );
  const genericFamilySelectionDraft = hasConcreteProductMatch
    ? null
    : buildGenericFamilySelectionDraft({
        question: safeQuestion,
        products: normalizedProducts,
      });

  if (genericFamilySelectionDraft) {
    return genericFamilySelectionDraft;
  }

  const splitOrderItems = buildSplitCompanionOrderItems({
    question: safeQuestion,
    products: normalizedProducts,
    linkedRules,
  });

  if (splitOrderItems.length > 1) {
    const relatedProductsById = new Map();
    splitOrderItems.forEach((item) => {
      (item.relatedProducts || []).forEach((relatedProduct) => {
        const relatedProductId = toSafeText(
          relatedProduct.productId || relatedProduct.id || "",
          120
        );
        if (relatedProductId && !relatedProductsById.has(relatedProductId)) {
          relatedProductsById.set(relatedProductId, relatedProduct);
        }
      });
    });

    return {
      shouldHandle: true,
      status: "ready",
      action: "add_many",
      quantity: splitOrderItems.reduce(
        (total, item) => total + Math.max(1, Number(item.quantity || 1) || 1),
        0
      ),
      items: splitOrderItems,
      relatedProducts: Array.from(relatedProductsById.values()).slice(0, 8),
    };
  }

  const product =
    concreteProductCandidate ||
    pickOrderProductCandidate(safeQuestion, normalizedProducts);

  if (!product) {
    return {
      shouldHandle: true,
      status: "not_found",
      quantity: extractOrderQuantity(safeQuestion),
      answer:
        "No encontre con claridad ese producto en el menu. Decime el nombre exacto o toca una opcion de las tarjetas.",
      relatedProducts: buildAssistantRelatedProducts({
        question: safeQuestion,
        answer: "",
        products: normalizedProducts,
        linkedRules,
      }),
    };
  }

  if (isBroadFamilyOrderQuestion(safeQuestion, product)) {
    return {
      shouldHandle: true,
      status: "not_found",
      quantity: extractOrderQuantity(safeQuestion),
      answer: buildBroadFamilyBrowseAnswer(safeQuestion),
      relatedProducts: buildAssistantRelatedProducts({
        question: `mostrame ${safeQuestion}`,
        answer: "",
        products: normalizedProducts,
        linkedRules,
      }),
    };
  }

  const standaloneCompanionItems = buildStandaloneCompanionOrderItems({
    question: safeQuestion,
    product,
    products: normalizedProducts,
    linkedRules,
  });

  if (standaloneCompanionItems.length > 1) {
    return {
      shouldHandle: true,
      status: "ready",
      action: "add_many",
      quantity: standaloneCompanionItems.reduce(
        (total, item) => total + Math.max(1, Number(item.quantity || 1)),
        0
      ),
      items: standaloneCompanionItems,
      relatedProducts: standaloneCompanionItems.map((item) =>
        summarizeRelatedProduct(item.product)
      ),
    };
  }

  const pairingContexts = buildMenuPairingContexts({
    question: safeQuestion,
    products: relationProducts,
    linkedRules,
    sourceProducts: [product],
  });
  const pairingsText = formatPairingContexts(pairingContexts);
  const ingredientSelectionPlan = buildPublicMenuAssistantIngredientSelectionPlan(
    safeQuestion,
    product
  );
  return {
    shouldHandle: true,
    status: "ready",
    action: "add",
    product,
    productId: toSafeText(product?.id || product?.productId, 120),
    quantity: extractOrderQuantity(safeQuestion),
    pairingsText,
    ingredientSelectionPlan,
    relatedProducts: buildAssistantRelatedProducts({
      question: safeQuestion,
      answer: pairingsText,
      products: normalizedProducts,
      linkedRules,
    }),
  };
};

const isWaiterHelpRequest = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  return (
    /\b(llamar|llama|llamen|necesito|quiero|busco|pedir)\b/.test(normalizedQuestion) &&
    /\b(mozo|mesero|camarero|persona|humano)\b/.test(normalizedQuestion)
  ) || /\b(atencion humana|ayuda humana|alguien que me atienda)\b/.test(normalizedQuestion);
};

const isProductBrowseRequest = (question = "") =>
  /\b(abrime|abri|abrir|mostrame|muestrame|mostrar|ver|mirar|detalle|detalles|ficha|foto|imagen)\b/.test(
    normalizeSearchText(question)
  );

const resolveProductsFromRelatedEntries = (relatedProducts = [], products = []) => {
  const productsById = new Map(
    (Array.isArray(products) ? products : []).map((product) => [
      toSafeText(product?.id || product?.productId || "", 120),
      product,
    ])
  );

  return (Array.isArray(relatedProducts) ? relatedProducts : [])
    .map((entry) => {
      const relatedProductId = toSafeText(
        entry?.productId || entry?.id || "",
        120
      );
      return relatedProductId ? productsById.get(relatedProductId) : null;
    })
    .filter(Boolean);
};

const buildResolvedAssistantAction = ({
  question = "",
  products = [],
  linkedRules = [],
  cartItems = [],
  relatedProducts = [],
  fallbackAction = null,
} = {}) => {
  products = getAssistantMenuCatalog(products, linkedRules).standaloneProducts;
  // V2: la IA conversa, pero las acciones visibles se resuelven con reglas locales.
  const safeQuestion = toSafeText(question, 320);
  const localOrderDraft = buildPublicMenuAssistantOrderDraft({
    question: safeQuestion,
    products,
    linkedRules,
    cartItems,
  });

  if (localOrderDraft?.shouldHandle) {
    return null;
  }

  if (isWaiterHelpRequest(safeQuestion)) {
    return {
      type: "call_waiter",
      productId: "",
      targetProductId: "",
      cartItemId: "",
      quantity: 1,
      confidence: 0.98,
      reason: "pedido_humano_local_v2",
    };
  }

  const relatedRealProducts = resolveProductsFromRelatedEntries(relatedProducts, products);

  if (isProductBrowseRequest(safeQuestion)) {
    const browseCandidates =
      relatedRealProducts.length > 0
        ? relatedRealProducts
        : rankProductsForQuestion(safeQuestion, products, { limit: 4 });
    const browseProduct = Array.isArray(browseCandidates) ? browseCandidates[0] : null;

    if (
      browseProduct &&
      !isBroadFamilyOrderQuestion(safeQuestion, browseProduct) &&
      (relatedRealProducts.length <= 1 || productNameLooksMentioned(safeQuestion, browseProduct))
    ) {
      return {
        type: "open_product",
        productId: toSafeText(
          browseProduct?.id || browseProduct?.productId,
          120
        ),
        targetProductId: "",
        cartItemId: "",
        quantity: 1,
        confidence: 0.82,
        reason: "detalle_producto_local_v2",
      };
    }
  }

  const intent = getQuestionIntent(safeQuestion);
  if (
    Array.isArray(relatedProducts) &&
    relatedProducts.length > 0 &&
    (intent.asksForList ||
      intent.asksForComparison ||
      intent.asksForBudget ||
      intent.asksForDrink ||
      intent.asksForDessert ||
      intent.asksForLight ||
      intent.asksForHearty ||
      inferGastronomyFamilies(safeQuestion).length > 0 ||
      fallbackAction?.type === "show_products")
  ) {
    return {
      type: "show_products",
      productId: "",
      targetProductId: "",
      cartItemId: "",
      quantity: 1,
      confidence: 0.8,
      reason: "mostrar_opciones_local_v2",
    };
  }

  return {
    type: "none",
    productId: "",
    targetProductId: "",
    cartItemId: "",
    quantity: 1,
    confidence: 0.4,
    reason: "sin_accion_local_v2",
  };
};

const formatCartSummary = (cartItems = [], submittedOrderSnapshot = null) => {
  const normalizedCartItems = (Array.isArray(cartItems) ? cartItems : [])
    .map(summarizeCartItem)
    .filter((item) => item.productName)
    .slice(0, 8);
  const sourceItems =
    normalizedCartItems.length > 0
      ? normalizedCartItems
      : Array.isArray(submittedOrderSnapshot?.items)
        ? submittedOrderSnapshot.items
            .map((item) => ({
              productName: toSafeText(item?.productName || item?.name || "Producto", 120),
              quantity: Math.max(1, Number(item?.quantity || 1)),
              selectionDetails: Array.isArray(item?.selectionDetails)
                ? item.selectionDetails
                    .map((detail) => toSafeText(detail, 120))
                    .filter(Boolean)
                    .slice(0, 6)
                : [],
            }))
            .slice(0, 8)
        : [];

  if (sourceItems.length === 0) {
    return "Todavia no veo productos en tu pedido. Si quieres, te ayudo a elegir.";
  }

  const totalAmount =
    normalizedCartItems.reduce((sum, item) => sum + toMoney(item.totalPrice), 0) ||
    toMoney(
      submittedOrderSnapshot?.pricing?.finalTotal ||
        submittedOrderSnapshot?.pricing?.listTotal ||
        submittedOrderSnapshot?.totalAmount
    );
  const formatSummaryItem = (item = {}) => {
    const safeQuantity = Math.max(1, Number(item?.quantity || 1));
    const safeProductName = toSafeText(item?.productName || item?.name || "Producto", 120);
    const quantityLabel =
      safeQuantity === 1 ? "una unidad de" : `${safeQuantity} unidades de`;

    return `${quantityLabel} ${safeProductName}`;
  };
  const summary = sourceItems
    .map((item) => formatSummaryItem(item))
    .join(", ");

  return totalAmount > 0
    ? `Ahora mismo tienes ${summary}. El total visible es ${currencyFormatter.format(
        totalAmount
      )}.`
    : `Ahora mismo tienes ${summary}.`;
};

const buildFollowUpSuggestions = ({ question = "", products = [], cartItems = [] } = {}) => {
  const matchedProducts = findMatchingProducts(question, products);
  const suggestions = [];

  if (matchedProducts[0]?.name) {
    suggestions.push(`¿Qué ingredientes tiene ${matchedProducts[0].name}?`);
    suggestions.push(`¿Cuánto sale ${matchedProducts[0].name}?`);
  }

  if (cartItems.length > 0) {
    suggestions.push("¿Cómo va mi pedido?");
  } else {
    suggestions.push("¿Qué me recomendás hoy?");
  }

  suggestions.push("Quiero algo liviano");
  suggestions.push("Llamar al mozo");

  return normalizeAssistantSuggestions(suggestions);
};

export const buildPublicMenuAssistantWelcomeMessage = ({
  branch = {},
  table = {},
  cartItems = [],
} = {}) => {
  const branchName = toSafeText(branch?.name || "la sucursal", 80);
  const tableLabel = toSafeText(
    table?.label || (table?.tableNumber ? `Mesa ${table.tableNumber}` : ""),
    80
  );

  if (cartItems.length > 0) {
    return `Soy el mozo virtual de ${branchName}${
      tableLabel ? ` para ${tableLabel}` : ""
    }. Puedo ayudarte con el menu, ingredientes, precios o revisar lo que ya cargaste en tu pedido.`;
  }

  return `Soy el mozo virtual de ${branchName}${
    tableLabel ? ` para ${tableLabel}` : ""
  }. Puedes preguntarme qué te recomiendo, qué lleva un plato, cuánto sale o si conviene llamar al mozo.`;
};

export const buildPublicMenuAssistantPromptSuggestions = ({
  products = [],
  linkedRules = [],
  cartItems = [],
} = {}) =>
  buildFollowUpSuggestions({
    question: cartItems.length > 0 ? "mi pedido" : "recomendacion",
    products: getAssistantMenuCatalog(products, linkedRules).standaloneProducts,
    cartItems,
  });

const isConversationFollowUpQuestion = (question = "") => {
  const normalizedQuestion = normalizeSearchText(question);

  if (!normalizedQuestion) {
    return false;
  }

  if (
    isFoodFamilyQuestion(normalizedQuestion) ||
    isDrinkFamilyQuestion(normalizedQuestion) ||
    isDessertQuestion(normalizedQuestion)
  ) {
    return false;
  }

  return (
    normalizedQuestion.length <= 24 ||
    /\b(y|esa|ese|eso|esas|esos|otra|otro|otros|otras|ahi|alli|dale|ok)\b/.test(
      normalizedQuestion
    ) ||
    /\b(con que viene|que trae|como viene|y para tomar|algo mas barato|algo mas liviano|algo mas fuerte|otra opcion|otra opcion parecida)\b/.test(
      normalizedQuestion
    )
  );
};

const buildConversationAwareQuestion = ({
  question = "",
  conversation = [],
  products = [],
  linkedRules = [],
} = {}) => {
  products = getAssistantMenuCatalog(products, linkedRules).standaloneProducts;
  const safeQuestion = toSafeText(question, 320);

  if (!isConversationFollowUpQuestion(safeQuestion)) {
    return safeQuestion;
  }

  const recentUserMessages = summarizeConversation(conversation)
    .filter((entry) => entry.role === "user")
    .map((entry) => entry.text)
    .filter(Boolean)
    .reverse();

  if (!recentUserMessages.length) {
    return safeQuestion;
  }

  const recentConversation = summarizeConversation(conversation);
  const latestAssistantMessage = [...recentConversation]
    .reverse()
    .find((entry) => entry.role === "assistant");
  const latestAssistantText = normalizeSearchText(latestAssistantMessage?.text || "");

  if (
    /\b(para tomar bien el pedido|cual queres|cual elegis|con que acompanamiento|opcion obligatoria)\b/.test(
      latestAssistantText
    )
  ) {
    return `${recentUserMessages[0]}. ${safeQuestion}`;
  }

  const hierarchy = buildMenuHierarchy(products);

  for (const previousQuestion of recentUserMessages) {
    const matchedProducts = rankProductsForQuestion(previousQuestion, products, {
      limit: 8,
    });
    const hierarchyFocus = resolveHierarchyFocus({
      question: previousQuestion,
      hierarchy,
      matchedProducts,
    });
    const exactProduct = matchedProducts.find(
      (product) =>
        productNameLooksMentioned(previousQuestion, product) &&
        !isBroadFamilyOrderQuestion(previousQuestion, product)
    );

    if (exactProduct?.name) {
      return `${safeQuestion} sobre ${exactProduct.name}`;
    }

    if (hierarchyFocus?.type === "subcategory" && hierarchyFocus?.entry) {
      return `${safeQuestion} sobre ${hierarchyFocus.entry.subcategoryName} en ${hierarchyFocus.entry.categoryName}`;
    }

    if (hierarchyFocus?.type === "category" && hierarchyFocus?.entry) {
      return `${safeQuestion} sobre ${hierarchyFocus.entry.categoryName}`;
    }
  }

  return safeQuestion;
};

export const buildPublicMenuAssistantContextualQuestion = (options = {}) =>
  buildConversationAwareQuestion(options);

const buildLocalPublicMenuAssistantReply = ({
  question,
  products = [],
  linkedRules = [],
  cartItems = [],
  submittedOrderSnapshot = null,
  conversation = [],
} = {}) => {
  const safeQuestion = toSafeText(question, 320);
  const contextualQuestion = buildConversationAwareQuestion({
    question: safeQuestion,
    conversation,
    products,
  });
  const focusedProducts = filterPublicMenuAssistantProductsForQuestion(
    contextualQuestion,
    products
  );
  const normalizedQuestion = normalizeSearchText(contextualQuestion);
  const intent = getQuestionIntent(contextualQuestion);
  const strictListProducts = intent.asksForList
    ? getStrictListProductsForQuestion(contextualQuestion, focusedProducts, { limit: 10 })
    : [];
  const matchedProducts =
    strictListProducts.length > 0
      ? strictListProducts
      : rankProductsForQuestion(contextualQuestion, focusedProducts, { limit: 8 });
  const recommendationProducts = pickRecommendationProducts(contextualQuestion, focusedProducts);
  const pairingContexts = buildMenuPairingContexts({
    question: contextualQuestion,
    products,
    linkedRules,
    sourceProducts: matchedProducts.length > 0 ? matchedProducts.slice(0, 3) : recommendationProducts,
  });
  const pairingsText = formatPairingContexts(pairingContexts);
  const appendPairings = (answer) =>
    pairingsText ? `${answer} ${pairingsText}.` : answer;
  const hierarchy = buildMenuHierarchy(focusedProducts);
  const hierarchyFocus = resolveHierarchyFocus({
    question: contextualQuestion,
    hierarchy,
    matchedProducts,
  });
  const buildHierarchyFocusAnswer = () => {
    if (!hierarchyFocus?.entry) {
      return "";
    }

    if (hierarchyFocus.type === "category") {
      const subcategoryNames = (hierarchyFocus.entry.subcategories || [])
        .map((subcategoryEntry) => toSafeText(subcategoryEntry.subcategoryName, 120))
        .filter(Boolean)
        .slice(0, 8);
      const categoryTitles = buildProductVariantSummary(
        hierarchyFocus.entry.products || []
      ).titles.slice(0, 6);

      if (subcategoryNames.length >= 2) {
        return [
          `Dentro de ${hierarchyFocus.entry.categoryName} tenes estas lineas: ${joinNaturalOptions(
            subcategoryNames,
            "y"
          )}.`,
          categoryTitles.length > 0
            ? `Ahora te voy mostrando opciones concretas como ${joinNaturalOptions(
                categoryTitles.slice(0, 4),
                "y"
              )}.`
            : "",
          "Te las dejo en el scroll para que abras la que quieras.",
        ]
          .filter(Boolean)
          .join(" ");
      }

      if (categoryTitles.length > 0) {
        return `Dentro de ${hierarchyFocus.entry.categoryName} tenes: ${joinNaturalOptions(
          categoryTitles,
          "y"
        )}. Te las dejo en el scroll para que las abras.`;
      }

      return "";
    }

    const variantSummary = buildProductVariantSummary(
      hierarchyFocus.entry.products || []
    );
    const productTitles = buildDistinctBaseProductTitles(
      hierarchyFocus.entry.products || []
    ).slice(0, 8);
    const linkedGroups = buildHierarchyLinkedGroups({
      sourceSubcategoryId: hierarchyFocus.entry.subcategoryId,
      hierarchy,
      linkedRules,
    });
    const linkedSummary = formatHierarchyLinkedGroups(linkedGroups);
    const shouldUseVariantPairings = !linkedSummary;

    return [
      productTitles.length > 0
        ? `Dentro de ${hierarchyFocus.entry.categoryName}, en ${hierarchyFocus.entry.subcategoryName} tenes: ${productTitles.join(
            ", "
          )}.`
        : `Dentro de ${hierarchyFocus.entry.categoryName}, en ${hierarchyFocus.entry.subcategoryName} no veo opciones visibles ahora.`,
      shouldUseVariantPairings && variantSummary.variantPairings.length > 0
        ? `${variantSummary.variantPairings.join(". ")}.`
        : "",
      linkedSummary ? `Combinaciones visibles: ${linkedSummary}` : "",
      productTitles.length > 0
        ? "Te las dejo en el scroll para que abras la opcion que quieras."
        : "",
    ]
      .filter(Boolean)
      .join(" ");
  };
  const wantsWaiterHelp =
    /(mozo|mesero|garzon|camarero|ayuda humana|persona|atencion)/i.test(
      normalizedQuestion
    );
  const asksForOrderStatus =
    /(mi pedido|pedido actual|carrito|que tengo|total|cuenta)/i.test(
      normalizedQuestion
    );
  const asksForPrice =
    /(cuanto|precio|sale|vale|cost)/i.test(normalizedQuestion);
  const asksForIngredients =
    /(ingred|lleva|tiene|trae|sin |se puede sacar|se puede agregar|personalizar)/i.test(
      normalizedQuestion
    );
  const asksForRecommendation =
    /(recom|suger|que pido|que me conviene|algo rico|algo para comer|quiero comer)/i.test(
      normalizedQuestion
    ) ||
    intent.asksForDrink ||
    intent.asksForDessert ||
    intent.asksForLight ||
    intent.asksForHearty;

  if (isBroadFoodListQuestion(contextualQuestion)) {
    return {
      answer: buildBroadFoodListAnswer(strictListProducts),
      suggestions: normalizeAssistantSuggestions([
        "Quiero algo liviano",
        "Quiero algo abundante",
        "Llamar al mozo",
      ]),
    };
  }

  if (wantsWaiterHelp) {
    return {
      answer:
        "Si prefieres ayuda humana, toca Llamar al mozo. Mientras tanto, yo te puedo orientar con precios, ingredientes, combinaciones y que te conviene pedir.",
      suggestions: normalizeAssistantSuggestions([
        "Llamar al mozo",
        "¿Qué me recomendás hoy?",
        "¿Cómo va mi pedido?",
      ]),
    };
  }

  if (asksForOrderStatus) {
    return {
      answer: formatCartSummary(cartItems, submittedOrderSnapshot),
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion,
        products: focusedProducts,
        cartItems,
      }),
    };
  }

  if (intent.asksForBudget) {
    const affordableProducts = (matchedProducts.length > 0
      ? matchedProducts
      : rankProductsForQuestion("barato economico accesible", focusedProducts, { limit: 8 })
    )
      .filter((product) => {
        const price = getProductPrice(product);
        return intent.budgetLimit > 0 ? price > 0 && price <= intent.budgetLimit : price > 0;
      })
      .sort((left, right) => getProductPrice(left) - getProductPrice(right))
      .slice(0, 5);

    if (affordableProducts.length > 0) {
      return {
        answer: `Si queres cuidar el precio, yo miraria estas opciones: ${affordableProducts
          .map((product) => formatProductLine(product))
          .join("; ")}. Si quieres, despues te marco cual rinde mas o cual te conviene segun lo que tengas ganas de comer.`,
        suggestions: buildFollowUpSuggestions({
          question: contextualQuestion,
          products: affordableProducts,
          cartItems,
        }),
      };
    }
  }

  if (matchedProducts.length > 0 && intent.asksForList) {
    const hierarchyAnswer = buildHierarchyFocusAnswer();

    return {
      answer: [
        hierarchyAnswer || "En esa linea no veo opciones visibles ahora.",
      ]
        .filter(Boolean)
        .join(" "),
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion,
        products: matchedProducts,
        cartItems,
      }),
    };
  }

  if (matchedProducts.length >= 2 && intent.asksForComparison) {
    const [firstProduct, secondProduct] = matchedProducts;
    return {
      answer: appendPairings(`Si las comparo rapido, tenes ${formatProductLine(firstProduct, {
        includeIngredients: true,
      })}. ${formatProductLine(secondProduct, {
        includeIngredients: true,
      })}. Si quieres algo mas contundente, yo iria por ${getProductPrice(firstProduct) >= getProductPrice(secondProduct) ? firstProduct.name : secondProduct.name}; si quieres cuidar precio, te conviene mas ${getProductPrice(firstProduct) <= getProductPrice(secondProduct) ? firstProduct.name : secondProduct.name}.`),
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion,
        products: matchedProducts,
        cartItems,
      }),
    };
  }

  if (matchedProducts.length > 0 && asksForPrice) {
    return {
      answer: `Hoy veo estos precios: ${matchedProducts
        .map((product) => {
          const price = formatProductPrice(product);
          return price
            ? `${product.name}: ${price}.`
            : `${product.name}: no veo un precio cargado en este momento.`;
        })
        .join(" ")} Si quieres, tambien te digo cual conviene mas por precio o por porcion.`,
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion,
        products: focusedProducts,
        cartItems,
      }),
    };
  }

  if (matchedProducts.length > 0 && asksForIngredients) {
    const product = matchedProducts[0];
    const ingredientsSummary = formatProductIngredientSummary(product);
    const ingredientPlan = buildPublicMenuAssistantIngredientSelectionPlan(
      contextualQuestion,
      product
    );
    const customizationSummary = Array.isArray(product?.ingredients)
      ? (() => {
          const removableCount = product.ingredients.filter(
            (ingredient) => ingredient?.canRemove === true
          ).length;
          const extraCount = product.ingredients.filter(
            (ingredient) => ingredient?.canAddMore === true
          ).length;

          if (!removableCount && !extraCount) {
            return "";
          }

          const parts = [];

          if (removableCount > 0) {
            parts.push(`puedes quitar ${removableCount} ingrediente${removableCount === 1 ? "" : "s"}`);
          }

          if (extraCount > 0) {
            parts.push(`puedes sumar extras en ${extraCount} ingrediente${extraCount === 1 ? "" : "s"}`);
          }

          return parts.length > 0 ? `Ademas, ${parts.join(" y ")}.` : "";
        })()
      : "";

    return {
      answer: appendPairings(
        [
          ingredientPlan.hasChanges
            ? `Si quieres ${product.name} ${ingredientPlan.summaryText}, te lo puedo dejar asi en el pedido.`
            : `${product.name}. ${ingredientsSummary}`,
          !ingredientPlan.hasChanges && customizationSummary
            ? customizationSummary
            : "",
          "Si quieres, tambien te digo con que combina mejor dentro del menu.",
        ]
          .filter(Boolean)
          .join(" ")
      ),
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion,
        products: focusedProducts,
        cartItems,
      }),
    };
  }

  if (asksForRecommendation && recommendationProducts.length > 0) {
    return {
      answer: appendPairings(recommendationProducts
        .map((product, index) => {
          const price = formatProductPrice(product);
          const description = toSafeText(product?.description || "", 110);
          const ingredients = getProductIngredients(product).slice(0, 3).join(", ");
          return `${index === 0 ? "Yo arrancaria por" : "Tambien miraria"} ${
            product.name
          }${price ? ` (${price})` : ""}${
            description
              ? `: ${description}.`
              : ingredients
                ? `: lleva ${ingredients}.`
                : "."
          }`;
        })
        .join(" ")),
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion,
        products: focusedProducts,
        cartItems,
      }),
    };
  }

  if (matchedProducts.length > 0) {
    const product = matchedProducts[0];
    const price = formatProductPrice(product);
    const decisionSummary = formatProductDecisionSummary(product);

    return {
      answer: appendPairings(`${product.name}${price ? ` sale ${price}` : ""}${
        decisionSummary ? `. ${decisionSummary}` : "."
      } Si quieres, te cuento ingredientes, te comparo opciones parecidas o te digo con que queda mejor.`),
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion,
        products: focusedProducts,
        cartItems,
      }),
    };
  }

  return {
    answer:
      "Puedo ayudarte a elegir mejor: te recomiendo platos, te cuento ingredientes, precios, combinaciones y tambien reviso tu pedido actual. Si quieres ayuda humana, usa Llamar al mozo.",
      suggestions: buildFollowUpSuggestions({
        question: contextualQuestion || normalizedQuestion || "recomendacion",
        products: focusedProducts,
        cartItems,
      }),
  };
};

const normalizeAssistantResponse = (
  value = {},
  fallbackSuggestions = [],
  products = [],
  question = "",
  linkedRules = []
) => {
  const focusedProducts = filterPublicMenuAssistantProductsForQuestion(
    question,
    products
  );
  const allowedProductIds = new Set(
    focusedProducts
      .map((product) => toSafeText(product?.id || product?.productId || "", 120))
      .filter(Boolean)
  );
  const safeDecision = restrictAssistantDecisionToMenu({
    ...value,
    visibleProductIds: normalizeAssistantStringList(value?.visibleProductIds || value?.productIds || value?.relatedProductIds, 16, 120),
    action: normalizeAssistantAction(value?.action || value?.cartAction),
  }, products);
  const answer = normalizeAssistantAnswerText(
    safeDecision?.answer || safeDecision?.message || safeDecision?.reply || ""
  );
  const suggestions = normalizeAssistantSuggestions(safeDecision?.suggestions).length
    ? normalizeAssistantSuggestions(safeDecision?.suggestions)
    : normalizeAssistantSuggestions(fallbackSuggestions);
  const visibleProductIds = normalizeAssistantStringList(
    safeDecision.visibleProductIds,
    16,
    120
  ).filter((productId) => allowedProductIds.has(productId));
  const aiRelatedProducts = buildAssistantRelatedProductsFromIds(
    visibleProductIds,
    focusedProducts,
    { includeDescription: !getQuestionIntent(question).asksForList }
  );
  const fallbackRelatedProducts = buildAssistantRelatedProducts({
    question,
    answer,
    suggestions,
    products: focusedProducts,
    linkedRules,
  });
  const action = safeDecision.action;
  const relatedProductsByKey = new Map();

  [...aiRelatedProducts, ...fallbackRelatedProducts].forEach((product) => {
    const key =
      toSafeText(product?.productId || product?.id || "", 120) ||
      normalizeSearchText(product?.name || "");

    if (!key || relatedProductsByKey.has(key)) {
      return;
    }

    relatedProductsByKey.set(key, product);
  });
  const relatedProducts = Array.from(relatedProductsByKey.values()).slice(
    0,
    getQuestionIntent(question).asksForList ? 10 : 8
  );
  const resolvedAnswer = isBroadFoodListQuestion(question)
    ? buildBroadFoodListAnswer(relatedProducts)
    : answer;

  return {
    answer: resolvedAnswer,
    suggestions,
    source: toSafeText(value?.source || "openai", 40) || "openai",
    model: toSafeText(value?.model || "", 80),
    decisionParsed: value?.decisionParsed === true,
    action,
    visibleProductIds,
    relatedProducts,
  };
};

export const createPublicMenuAssistantReply = async ({
  qrToken = "",
  deliveryToken = "",
  question = "",
  branch = {},
  table = {},
  products = [],
  linkedRules = [],
  cartItems = [],
  submittedOrderSnapshot = null,
  customerName = "",
  customerSessionId = "",
  conversation = [],
} = {}) => {
  products = getAssistantMenuCatalog(products, linkedRules).products;
  const safeQuestion = toSafeText(question, 320);
  const contextualQuestion = buildConversationAwareQuestion({
    question: safeQuestion,
    conversation,
    products,
  });

  if (!safeQuestion) {
    throw new Error("Escribe lo que quieres preguntarle al mozo virtual.");
  }

  if (isBroadFoodListQuestion(contextualQuestion)) {
    const focusedProducts = filterPublicMenuAssistantProductsForQuestion(
      contextualQuestion,
      products
    );
    const visibleProducts = focusedProducts.slice(0, 10);

    return {
      answer: buildBroadFoodListAnswer(visibleProducts),
      suggestions: normalizeAssistantSuggestions([
        "Quiero algo liviano",
        "Quiero algo abundante",
        "Llamar al mozo",
      ]),
      action: visibleProducts.length > 0
        ? {
            type: "show_products",
            productId: "",
            targetProductId: "",
            cartItemId: "",
            quantity: 1,
            confidence: 1,
            reason: "lista_comida_local",
          }
        : null,
      visibleProductIds: visibleProducts
        .map((product) => toSafeText(product?.id || product?.productId || "", 120))
        .filter(Boolean),
      relatedProducts: visibleProducts.map((product) =>
        summarizeRelatedProduct(product, { includeDescription: false })
      ),
      fallback: false,
      source: "menu",
      model: "",
      degradedReason: "",
    };
  }

  const fallbackSuggestions = buildFollowUpSuggestions({
    question: contextualQuestion,
    products: products.filter(isAssistantStandaloneProduct),
    cartItems,
  });

  const baseContext = {
    question: contextualQuestion,
    products: Array.isArray(products) ? products : [],
    linkedRules: Array.isArray(linkedRules) ? linkedRules : [],
    cartItems: Array.isArray(cartItems) ? cartItems : [],
    submittedOrderSnapshot,
    conversation,
  };

  if (!supabase) {
    const localReply = buildLocalPublicMenuAssistantReply(baseContext);
    const fallbackRelatedProducts = buildAssistantRelatedProducts({
      question: contextualQuestion,
      answer: localReply.answer,
      suggestions: localReply.suggestions,
      products,
      linkedRules,
    });
    return {
      ...localReply,
      action: buildResolvedAssistantAction({
        question: contextualQuestion,
        products,
        linkedRules,
        cartItems,
        relatedProducts: fallbackRelatedProducts,
        fallbackAction: localReply.action || null,
      }),
      relatedProducts: fallbackRelatedProducts,
      fallback: true,
      source: "local",
      degradedReason: "Supabase no esta configurado.",
    };
  }

  try {
    const { data, error } = await supabase.functions.invoke(
      PUBLIC_MENU_AI_FUNCTION_NAME,
      {
        body: {
          qrToken: toSafeText(qrToken, 160),
          deliveryToken: toSafeText(deliveryToken, 160),
          question: safeQuestion,
          branch: {
            id: toSafeText(branch?.id || "", 120),
            name: toSafeText(branch?.name || "", 80),
          },
          table: {
            id: toSafeText(table?.id || "", 120),
            label: toSafeText(table?.label || "", 80),
            tableNumber: Number(table?.tableNumber || 0) || null,
          },
          customerName: toSafeText(customerName, 80),
          customerSessionId: toSafeText(customerSessionId, 160),
          cartItems: (Array.isArray(cartItems) ? cartItems : [])
            .map(summarizeCartItem)
            .slice(0, 12),
          submittedOrder: summarizeSubmittedOrder(submittedOrderSnapshot),
          conversation: summarizeConversation(conversation),
        },
      }
    );

    if (error) {
      const errorPayload = await extractFunctionErrorPayload(error, data);

      throw new Error(
        normalizeFunctionError(
          error,
          errorPayload,
          "No pudimos responder desde la IA remota."
        )
      );
    }

    const normalized = normalizeAssistantResponse(
      data,
      fallbackSuggestions,
      products,
      contextualQuestion,
      linkedRules
    );
    const resolvedAction = buildResolvedAssistantAction({
      question: contextualQuestion,
      products,
      linkedRules,
      cartItems,
      relatedProducts: normalized.relatedProducts,
      fallbackAction: normalized.action,
    });

    if (!normalized.answer) {
      throw new Error("La IA no devolvio una respuesta util.");
    }

    if (looksLikeEnglishAssistantReply(normalized.answer)) {
      throw new Error("La IA remota devolvio texto fuera de espanol.");
    }

    if (looksLikeGarbageAssistantReply(normalized.answer)) {
      throw new Error("La IA remota devolvio texto fuera de contexto.");
    }

    return {
      ...normalized,
      action: resolvedAction,
      fallback: false,
      degradedReason: "",
    };
  } catch (error) {
    const localReply = buildLocalPublicMenuAssistantReply(baseContext);
    const fallbackRelatedProducts = buildAssistantRelatedProducts({
      question: contextualQuestion,
      answer: localReply.answer,
      suggestions: localReply.suggestions,
      products,
      linkedRules,
    });
    return {
      ...localReply,
      action: buildResolvedAssistantAction({
        question: contextualQuestion,
        products,
        linkedRules,
        cartItems,
        relatedProducts: fallbackRelatedProducts,
        fallbackAction: localReply.action || null,
      }),
      relatedProducts: fallbackRelatedProducts,
      fallback: true,
      source: "local",
      degradedReason:
        toSafeText(error?.message, 240) ||
        "No pudimos usar la IA remota, asi que respondemos con el respaldo local.",
    };
  }
};
